/* eslint-disable camelcase, no-continue */
/// <reference path="./timeline-types.js" />
/* eslint-disable operator-assignment, no-restricted-syntax */

import { Set as ImmutableSet, fromJS, isImmutable } from "immutable";
import { DEFAULT_DURATION, RULER_OPTIONS } from "./timeline-constants";
import {
  buildLocalAiReframe,
  buildLocalSmartfocus,
  buildLocalSubtitles,
  isAudioOnly,
  isImageOnly,
  isPropertyOnly,
  isReframeDropItem,
  isUpImageSVG,
  isVideoOnly,
  randomString,
} from "./timeline-helper";
import { applyCropToDropItem } from "../../helper/TransformManagerHelper";
import { REFRAME_LAYOUT } from "../../constants";

/**
 * function to handle track change if items are moved to new track
 * @param {HandleNewTracksParams} params
 */
const handleNewTracks = (params = {}) => {
  const { newTracks } = params;
  let { updaterObject } = params;
  let { project } = params;

  for (const container of Reflect.ownKeys(newTracks)) {
    /** indices of new tracks in ascending order */
    const trackIndices = Reflect.ownKeys(newTracks[container]);
    const items = project.get(container);
    let newItems = items;

    for (const newTrackIndex of trackIndices) {
      const newTrackDetail = newTracks[container][newTrackIndex];
      const alreadyMovedItemIds = newTrackDetail.ids;

      for (const [itemId, item] of items.entrySeq()) {
        if (
          item.get("track") >= newTrackDetail.trackIndex &&
          !alreadyMovedItemIds.includes(itemId)
        ) {
          const newTrack = item.get("track") + 1;
          newItems = newItems.setIn([itemId, "track"], newTrack);
          updaterObject = updaterObject.setIn(
            [container, itemId, "data", "track"],
            newTrack
          );
        }
      }
    }

    project = project.set(container, newItems);
  }

  return {
    project,
    updaterObject,
  };
};

/**
 * removes overlap of items timing (e.g. when an item is moved to different track)
 * @param {TrackChangeItemOverlap} params
 */
const trackChangeItemOverlap = (params = {}) => {
  const { movedItemIds, items, timelineInterval } = params;
  let { updaterObject } = params;
  let newItems = items;
  const itemIdsGroupedByTracks = {};

  const uniqueMovedItemIds = [...new Set(movedItemIds)];

  for (const movedItemId of uniqueMovedItemIds) {
    const movedItem = newItems.get(movedItemId);

    if (!movedItem) {
      continue;
    }

    // Skip if moved item is a scene item - scenes move with parent
    if (movedItem.get("sceneId") || movedItem.get("sceneData") || movedItem.get("id")?.startsWith("scene")) {
      continue;
    }

    const trackIndex = movedItem.get("track");

    let currentTrackItemIds = itemIdsGroupedByTracks[trackIndex];
    if (!currentTrackItemIds) {
      currentTrackItemIds = newItems
        .filter((item) => item.get("track") === trackIndex)
        .keySeq()
        .toArray();
      itemIdsGroupedByTracks[trackIndex] = currentTrackItemIds;
    }

    for (const itemId of currentTrackItemIds) {
      if (itemId !== movedItemId && newItems.get(itemId)) {
        const item = newItems.get(itemId);

        // Skip scene items (children of reframe) - they should move with parent, not be deleted due to overlap
        if (item.get("sceneId") || item.get("sceneData") || item.get("id")?.startsWith("scene")) {
          continue;
        }

        let left;
        let right;
        let leftUpdater = fromJS({});
        let rightUpdater = leftUpdater;

        let isOverlapping = !(
          movedItem.get("enterStart") >= item.get("exitEnd") ||
          movedItem.get("exitEnd") <= item.get("enterStart")
        );

        // ─── LEFT NEIGHBOR ───────────────────────────────────────────────────────
        if (
          item.get("enterStart") < movedItem.get("enterStart") &&
          movedItem.get("enterStart") < item.get("exitEnd")
        ) {
          left = item;
          const leftExitEnd = movedItem.get("enterStart");
          if (leftExitEnd !== left.get("exitEnd")) {
            // ✅ FIX: Uncommented - Video2 expanding left must shrink Video1's exitEnd
            left = item.set("exitEnd", leftExitEnd);
            leftUpdater = leftUpdater.set("exitEnd", leftExitEnd);
          }

          if (isVideoOnly(left.get("type"), left.get("subType"))) {
            // ✅ FIX: Use leftExitEnd (new value) instead of left.get("exitEnd") (old value)
            const videoEnd =
              left.get("videoStart") +
              (leftExitEnd - left.get("enterStart"));
            if (videoEnd !== left.get("videoEnd")) {
              left = left.set("videoEnd", videoEnd);
              leftUpdater = leftUpdater.set("videoEnd", videoEnd);
            }
          }

          if (
            leftExitEnd - left.get("enterStart") <
            timelineInterval
          ) {
            left = undefined;
            leftUpdater = undefined;
          } else {
            if (
              left.get("enterEffectName") &&
              left.get("enterEffectName") !== "no_Effect" &&
              left.get("enterEnd") > movedItem.get("enterStart")
            ) {
              const enterEnd = movedItem.get("enterStart");
              if (enterEnd !== left.get("enterEnd")) {
                left = left.set("enterEnd", enterEnd);
                leftUpdater = leftUpdater.set("enterEnd", enterEnd);
              }

              if (
                left.get("enterEnd") - left.get("enterStart") <
                timelineInterval
              ) {
                left = left
                  .set("enterEffectName", "no_Effect")
                  .set("enterEnd", 0);
                leftUpdater = leftUpdater
                  .set("enterEffectName", "no_Effect")
                  .set("enterEnd", 0);
              }
            }

            if (
              left.get("exitEffectName") &&
              left.get("exitEffectName") !== "no_Effect" &&
              left.get("exitEnd") - left.get("exitStart") <
              timelineInterval
            ) {
              left = left
                .set("exitEffectName", "no_Effect")
                .set("exitStart", 0);
              leftUpdater = leftUpdater
                .set("exitEffectName", "no_Effect")
                .set("exitStart", 0);
            }
          }
          isOverlapping = true;
        }

        // ─── RIGHT NEIGHBOR ──────────────────────────────────────────────────────
        if (
          item.get("enterStart") < movedItem.get("exitEnd") &&
          movedItem.get("exitEnd") < item.get("exitEnd")
        ) {
          right = item;

          const enterStart = movedItem.get("exitEnd");
          if (enterStart !== item.get("enterStart")) {
            right = item.set("enterStart", enterStart);
            rightUpdater = rightUpdater.set("enterStart", enterStart);
          }

          if (isVideoOnly(right.get("type"), right.get("subType"))) {
            const videoStart =
              right.get("videoEnd") -
              (right.get("exitEnd") - right.get("enterStart"));

            if (videoStart !== right.get("videoStart")) {
              right = right.set("videoStart", videoStart);
              rightUpdater = rightUpdater.set("videoStart", videoStart);
            }
          }

          if (
            right.get("exitEnd") - right.get("enterStart") <
            timelineInterval
          ) {
            right = undefined;
            rightUpdater = undefined;
          } else {
            if (
              right.get("exitEffectName") &&
              right.get("exitEffectName") !== "no_Effect" &&
              right.get("exitStart") < movedItem.get("exitEnd")
            ) {
              const exitStart = movedItem.get("exitEnd");
              if (exitStart !== right.get("exitStart")) {
                right = right.set("exitStart", movedItem.get("exitEnd"));
                rightUpdater = rightUpdater.set(
                  "exitStart",
                  movedItem.get("exitEnd")
                );
              }
              if (
                right.get("exitEnd") - right.get("exitStart") <
                timelineInterval
              ) {
                right = right
                  .set("exitEffectName", "no_Effect")
                  .set("exitStart", 0);
                rightUpdater = rightUpdater
                  .set("exitEffectName", "no_Effect")
                  .set("exitStart", 0);
              }
            }

            if (
              right.get("enterEffectName") &&
              right.get("enterEffectName") !== "no_Effect" &&
              right.get("enterEnd") - right.get("enterStart") <
              timelineInterval
            ) {
              right = right
                .set("enterEffectName", "no_Effect")
                .set("enterEnd", 0);
              rightUpdater = rightUpdater
                .set("enterEffectName", "no_Effect")
                .set("enterEnd", 0);
            }
          }
          isOverlapping = true;
        }

        // ─── OVERLAP RESOLUTION ──────────────────────────────────────────────────
        if (isOverlapping) {

          // ── CASE 1: Item fully consumed (deleted) ──────────────────────────────
          if (!left && !right) {
            newItems = newItems.delete(item.get("id"));
            updaterObject = updaterObject.setIn(
              ["workspaceItems", item.get("id"), "isDelete"],
              true
            );

            // Also delete all child scenes associated with this deleted item
            const scenes = newItems.filter(
              (child) =>
                child.get("sceneData") &&
                (child.get("originalId") === item.get("id") ||
                  child.get("dropId") === item.get("id"))
            );
            if (scenes.size > 0) {
              for (const [childId] of scenes.entrySeq()) {
                newItems = newItems.delete(childId);
                updaterObject = updaterObject.setIn(
                  ["workspaceItems", childId, "isDelete"],
                  true
                );
              }
            }
          }

          // ── CASE 2: Item split into left + right ───────────────────────────────
          else if (left && right && leftUpdater && rightUpdater) {
            right = right.set("id", randomString("split"));
            right = right.set("dropId", right.get("id"));
            if (item.get("isReframe")) {
              const origReframeId = item.get("reframeOriginalId") || item.get("id");
              right = right.set("reframeOriginalId", origReframeId);
            }
            newItems = newItems
              .set(left.get("id"), left)
              .set(right.get("id"), right);
            currentTrackItemIds.push(right.get("id"));

            if (leftUpdater.size > 0) {
              const prevLeftUpdater = updaterObject.getIn(["workspaceItems", left.get("id"), "data"]);
              if (prevLeftUpdater) {
                leftUpdater = prevLeftUpdater.mergeDeep(leftUpdater);
              }
              updaterObject = updaterObject.setIn(
                ["workspaceItems", left.get("id"), "data"],
                leftUpdater
              );
            }
            updaterObject = updaterObject.setIn(
              ["workspaceItems", right.get("id"), "isNew"],
              true
            );
            updaterObject = updaterObject.setIn(
              ["workspaceItems", right.get("id"), "isSplit"],
              true
            );
            updaterObject = updaterObject.setIn(
              ["workspaceItems", right.get("id"), "data"],
              right
            );

            const isReframe = item.get("isReframe") || item.get("dropId");
            if (isReframe) {
              const scenes = newItems.filter(
                (child) =>
                  child.get("sceneData") &&
                  (child.get("originalId") === item.get("id") ||
                    child.get("dropId") === item.get("id"))
              );

              if (scenes.size > 0) {
                const newExitEnd = leftUpdater.get("exitEnd");
                const newEnterStart = rightUpdater.get("enterStart");

                for (const [childId, child] of scenes.entrySeq()) {
                  const childEnterStart = child.get("enterStart") || 0;
                  const childExitEnd = child.get("exitEnd") || 0;

                  // ── Left piece scenes ──
                  if (childEnterStart >= newExitEnd) {
                    newItems = newItems.delete(childId);
                    updaterObject = updaterObject.setIn(
                      ["workspaceItems", childId, "isDelete"],
                      true
                    );
                  } else if (childExitEnd > newExitEnd) {
                    const delta = childExitEnd - newExitEnd;
                    let updatedChild = child;
                    let childUpdater = fromJS({});

                    if (child.get("exitStart") !== undefined) {
                      const newExitStart =
                        child.get("exitStart") === childExitEnd
                          ? newExitEnd
                          : Math.min(child.get("exitStart"), newExitEnd);
                      updatedChild = updatedChild.set("exitStart", newExitStart);
                      childUpdater = childUpdater.set("exitStart", newExitStart);
                    }

                    if (child.get("videoEnd") !== undefined) {
                      const newVideoEnd = child.get("videoEnd") - delta;
                      updatedChild = updatedChild.set("videoEnd", newVideoEnd);
                      childUpdater = childUpdater.set("videoEnd", newVideoEnd);
                    }
                    newItems = newItems.set(childId, updatedChild);
                    const prevUpdater =
                      updaterObject.getIn(["workspaceItems", childId, "data"]) ||
                      fromJS({});
                    updaterObject = updaterObject.setIn(
                      ["workspaceItems", childId, "data"],
                      prevUpdater.mergeDeep(childUpdater)
                    );
                  }

                  // ── Right piece scenes ──
                  if (childExitEnd > newEnterStart) {
                    const rightChildId = `${Math.random()
                      .toString(36)
                      .substring(2, 9)}scene-${right.get("id")}`;
                    let rightChild = child
                      .set("id", rightChildId)
                      .set("dropId", right.get("id"))
                      .set("originalId", right.get("originalId") || right.get("id"));

                    if (childEnterStart < newEnterStart) {
                      const delta = newEnterStart - childEnterStart;
                      rightChild = rightChild.set("enterStart", newEnterStart);

                      if (rightChild.get("enterEnd") !== undefined) {
                        const newEnterEnd =
                          rightChild.get("enterEnd") === childEnterStart
                            ? newEnterStart
                            : Math.max(rightChild.get("enterEnd"), newEnterStart);
                        rightChild = rightChild.set("enterEnd", newEnterEnd);
                      }

                      if (rightChild.get("videoStart") !== undefined) {
                        rightChild = rightChild.set(
                          "videoStart",
                          rightChild.get("videoStart") + delta
                        );
                      }
                    }

                    newItems = newItems.set(rightChildId, rightChild);
                    updaterObject = updaterObject.setIn(
                      ["workspaceItems", rightChildId, "isNew"],
                      true
                    );
                    updaterObject = updaterObject.setIn(
                      ["workspaceItems", rightChildId, "data"],
                      rightChild
                    );
                  }
                }
              }
            }
          }

          // ── CASE 3: Only one side (trim) ───────────────────────────────────────
          else if ((left && leftUpdater) || (right && rightUpdater)) {
            let updatedItem = left;
            let updater = leftUpdater;

            if (right && rightUpdater) {
              updatedItem = right;
              updater = rightUpdater;
            }

            newItems = newItems.set(updatedItem.get("id"), updatedItem);
            if (updater.size > 0) {
              const prevUpdater = updaterObject.getIn([
                "workspaceItems",
                updatedItem.get("id"),
                "data",
              ]);
              if (prevUpdater) {
                updater = prevUpdater.mergeDeep(updater);
              }
              updaterObject = updaterObject.setIn(
                ["workspaceItems", updatedItem.get("id"), "data"],
                updater
              );
            }

            const isReframe = item.get("isReframe") || item.get("dropId");
            if (isReframe) {
              const scenes = newItems.filter(
                (child) =>
                  child.get("sceneData") &&
                  (child.get("originalId") === item.get("id") ||
                    child.get("dropId") === item.get("id"))
              );

              if (scenes.size > 0) {

                // ── RIGHT trim: movedItem expanded/dragged into right neighbor ──
                // movedItem's exitEnd now cuts into item (right neighbor).
                // Scenes straddling the boundary must be SPLIT:
                //   left piece  → assigned to movedItem
                //   right piece → stays with right neighbor (item / updatedItem)
                if (updatedItem === right && rightUpdater.get("enterStart") !== undefined) {
                  const newEnterStart = rightUpdater.get("enterStart"); // = movedItem.exitEnd
                  // Set isMovedItemReframe to false to avoid transferring scenes between independent clips, preventing scene collapse
                  const isMovedItemReframe = false;

                  for (const [childId, child] of scenes.entrySeq()) {
                    const childEnterStart = child.get("enterStart") || 0;
                    const childExitEnd = child.get("exitEnd") || 0;

                    if (childExitEnd <= newEnterStart) {
                      // Scene is entirely before the boundary → give to movedItem if reframe, else delete
                      if (isMovedItemReframe) {
                        const updatedChild = child
                          .set("dropId", movedItemId)
                          .set("originalId", movedItemId);
                        newItems = newItems.set(childId, updatedChild);
                        const prevUpdater =
                          updaterObject.getIn(["workspaceItems", childId, "data"]) ||
                          fromJS({});
                        updaterObject = updaterObject.setIn(
                          ["workspaceItems", childId, "data"],
                          prevUpdater.mergeDeep(
                            fromJS({ dropId: movedItemId, originalId: movedItemId })
                          )
                        );
                      } else {
                        newItems = newItems.delete(childId);
                        updaterObject = updaterObject.setIn(
                          ["workspaceItems", childId, "isDelete"],
                          true
                        );
                      }

                    } else if (childEnterStart < newEnterStart && childExitEnd > newEnterStart) {
                      // ── Scene straddles the boundary → SPLIT ──────────────────
                      const splitDelta = newEnterStart - childEnterStart;

                      // LEFT piece: childEnterStart → newEnterStart, belongs to movedItem
                      if (isMovedItemReframe) {
                        const leftPieceId = `scene-left-${randomString("split")}`;
                        let leftPiece = child
                          .set("id", leftPieceId)
                          .set("dropId", movedItemId)
                          .set("originalId", movedItemId)
                          .set("exitEnd", newEnterStart);

                        if (child.get("exitStart") !== undefined) {
                          const newExitStart = Math.min(
                            child.get("exitStart"),
                            newEnterStart
                          );
                          leftPiece = leftPiece.set("exitStart", newExitStart);
                        }

                        if (child.get("videoEnd") !== undefined) {
                          // trim videoEnd by how much we cut from the right
                          const cutDuration = childExitEnd - newEnterStart;
                          leftPiece = leftPiece.set(
                            "videoEnd",
                            child.get("videoEnd") - cutDuration
                          );
                        }

                        newItems = newItems.set(leftPieceId, leftPiece);
                        updaterObject = updaterObject
                          .setIn(["workspaceItems", leftPieceId, "isNew"], true)
                          .setIn(["workspaceItems", leftPieceId, "data"], leftPiece);
                      }

                      // RIGHT piece: newEnterStart → childExitEnd, stays with right neighbor
                      let rightPiece = child
                        .set("enterStart", newEnterStart)
                        .set("dropId", updatedItem.get("id"))
                        .set("originalId", updatedItem.get("originalId") || updatedItem.get("id"));

                      if (child.get("enterEnd") !== undefined) {
                        const newEnterEnd = Math.max(
                          child.get("enterEnd"),
                          newEnterStart
                        );
                        rightPiece = rightPiece.set("enterEnd", newEnterEnd);
                      }

                      if (child.get("videoStart") !== undefined) {
                        rightPiece = rightPiece.set(
                          "videoStart",
                          child.get("videoStart") + splitDelta
                        );
                      }

                      newItems = newItems.set(childId, rightPiece);
                      const prevUpdater =
                        updaterObject.getIn(["workspaceItems", childId, "data"]) ||
                        fromJS({});
                      updaterObject = updaterObject.setIn(
                        ["workspaceItems", childId, "data"],
                        prevUpdater.mergeDeep(
                          fromJS({
                            enterStart: newEnterStart,
                            dropId: updatedItem.get("id"),
                            originalId: updatedItem.get("originalId") || updatedItem.get("id"),
                            ...(child.get("videoStart") !== undefined && {
                              videoStart: child.get("videoStart") + splitDelta,
                            }),
                          })
                        )
                      );

                    } else if (childEnterStart >= newEnterStart) {
                      // Scene is entirely after boundary → stays with right neighbor (no change needed)
                    }
                  }
                }

                // ── LEFT trim: Video2 expanding left into Video1 ──────────────
                // Video1 (left neighbor / updatedItem) shrinks its exitEnd.
                // Scenes of Video1 that straddle the new boundary must be SPLIT:
                //   left piece  → stays with Video1 (trimmed at newExitEnd)
                //   right piece → assigned to movedItem (Video2) from newExitEnd onward
                if (updatedItem === left && leftUpdater.get("exitEnd") !== undefined) {
                  const newExitEnd = leftUpdater.get("exitEnd"); // = movedItem.enterStart
                  const isMovedItemReframe = false;

                  for (const [childId, child] of scenes.entrySeq()) {
                    const childEnterStart = child.get("enterStart") || 0;
                    const childExitEnd = child.get("exitEnd") || 0;

                    if (childEnterStart >= newExitEnd) {
                      // Scene is entirely inside Video2's new region → reassign to movedItem if reframe, else delete
                      if (isMovedItemReframe) {
                        const updatedChild = child
                          .set("dropId", movedItemId)
                          .set("originalId", movedItemId);
                        newItems = newItems.set(childId, updatedChild);
                        const prevUpdater =
                          updaterObject.getIn(["workspaceItems", childId, "data"]) ||
                          fromJS({});
                        updaterObject = updaterObject.setIn(
                          ["workspaceItems", childId, "data"],
                          prevUpdater.mergeDeep(
                            fromJS({ dropId: movedItemId, originalId: movedItemId })
                          )
                        );
                      } else {
                        newItems = newItems.delete(childId);
                        updaterObject = updaterObject.setIn(
                          ["workspaceItems", childId, "isDelete"],
                          true
                        );
                      }

                    } else if (childEnterStart < newExitEnd && childExitEnd > newExitEnd) {
                      // ── Scene straddles the boundary → SPLIT ──────────────────
                      const splitDelta = newExitEnd - childEnterStart;

                      // LEFT piece: childEnterStart → newExitEnd, stays with Video1
                      let leftPiece = child
                        .set("exitEnd", newExitEnd)
                        .set("dropId", updatedItem.get("id"))
                        .set("originalId", updatedItem.get("originalId") || updatedItem.get("id"));

                      if (child.get("exitStart") !== undefined) {
                        const newExitStart = Math.min(child.get("exitStart"), newExitEnd);
                        leftPiece = leftPiece.set("exitStart", newExitStart);
                      }

                      if (child.get("videoEnd") !== undefined) {
                        const cutDuration = childExitEnd - newExitEnd;
                        leftPiece = leftPiece.set("videoEnd", child.get("videoEnd") - cutDuration);
                      }

                      newItems = newItems.set(childId, leftPiece);
                      const prevLeftUpdater =
                        updaterObject.getIn(["workspaceItems", childId, "data"]) ||
                        fromJS({});
                      updaterObject = updaterObject.setIn(
                        ["workspaceItems", childId, "data"],
                        prevLeftUpdater.mergeDeep(
                          fromJS({
                            exitEnd: newExitEnd,
                            dropId: updatedItem.get("id"),
                            originalId: updatedItem.get("originalId") || updatedItem.get("id"),
                            ...(child.get("exitStart") !== undefined && {
                              exitStart: Math.min(child.get("exitStart"), newExitEnd),
                            }),
                            ...(child.get("videoEnd") !== undefined && {
                              videoEnd: child.get("videoEnd") - (childExitEnd - newExitEnd),
                            }),
                          })
                        )
                      );

                      // RIGHT piece: newExitEnd → childExitEnd, assigned to movedItem (Video2)
                      if (isMovedItemReframe) {
                        const rightPieceId = `scene-right-${randomString("split")}`;
                        let rightPiece = child
                          .set("id", rightPieceId)
                          .set("enterStart", newExitEnd)
                          .set("dropId", movedItemId)
                          .set("originalId", movedItemId);

                        if (child.get("enterEnd") !== undefined) {
                          const newEnterEnd = Math.max(child.get("enterEnd"), newExitEnd);
                          rightPiece = rightPiece.set("enterEnd", newEnterEnd);
                        }

                        if (child.get("videoStart") !== undefined) {
                          rightPiece = rightPiece.set(
                            "videoStart",
                            child.get("videoStart") + splitDelta
                          );
                        }

                        newItems = newItems.set(rightPieceId, rightPiece);
                        updaterObject = updaterObject
                          .setIn(["workspaceItems", rightPieceId, "isNew"], true)
                          .setIn(["workspaceItems", rightPieceId, "data"], rightPiece);
                      }

                    }
                    // else: childExitEnd <= newExitEnd → scene fully inside Video1, no change
                  }
                }
              }
            }
          }
        }
      }
    }
  }

  return {
    newItems,
    updaterObject,
  };
};

/**
 * removes overlap of media timing
 * (e.g. when an audio is moved to different track or obj is moved to video track)
 * @param {TrackChangeMediaOverlap} params
 */
const trackChangeMediaOverlap = (params = {}) => {
  const { movedMediaIds, mediaData, container, timelineInterval } = params;
  let { updaterObject } = params;
  let newMedia = mediaData;

  const mediaIdsGroupedByTracks = {};
  let mediaStart = "musicStart";
  let mediaEnd = "musicEnd";

  if (container === "workspaceBG") {
    mediaStart = "videoStart";
    mediaEnd = "videoEnd";
  }

  for (const movedMediaId of movedMediaIds) {
    const movedMedia = mediaData.get(movedMediaId);
    const trackIndex =
      container === "workspaceBG" ? 0 : movedMedia.get("track");

    let currentTrackMediaIds = mediaIdsGroupedByTracks[trackIndex];
    if (!currentTrackMediaIds) {
      if (container === "workspaceBG") {
        currentTrackMediaIds = newMedia.keySeq().toArray();
      } else {
        currentTrackMediaIds = newMedia
          .filter((item) => item.get("track") === trackIndex)
          .keySeq()
          .toArray();
      }
      mediaIdsGroupedByTracks[trackIndex] = currentTrackMediaIds;
    }

    for (const mediaId of currentTrackMediaIds) {
      if (mediaId !== movedMediaId && newMedia.get(mediaId)) {
        const media = newMedia.get(mediaId);

        let left;
        let right;
        let leftUpdater = fromJS({});
        let rightUpdater = leftUpdater;

        let isOverlapping = !(
          // moved media is after current media and does not overlap
          (
            movedMedia.get("playStart") >= media.get("playEnd") ||
            // moved media is before current media and does not overlap
            movedMedia.get("playEnd") <= media.get("playStart")
          )
        );

        if (
          media.get("playStart") < movedMedia.get("playStart") &&
          movedMedia.get("playStart") < media.get("playEnd")
        ) {
          left = media;
          const playEnd = movedMedia.get("playStart");

          if (playEnd !== left.get("playEnd")) {
            left = media.set("playEnd", playEnd);
            leftUpdater = leftUpdater.set("playEnd", playEnd);
          }

          const mediaEndTime =
            left.get(mediaStart) +
            (left.get("playEnd") - left.get("playStart"));

          if (mediaEndTime !== left.get(mediaEnd)) {
            left = left.set(mediaEnd, mediaEndTime);
            leftUpdater = leftUpdater.set(mediaEnd, mediaEndTime);
          }

          if (
            left.get("playEnd") - left.get("playStart") <
            timelineInterval
          ) {
            left = undefined;
            leftUpdater = undefined;
          }
          isOverlapping = true;
        }

        if (
          media.get("playStart") < movedMedia.get("playEnd") &&
          movedMedia.get("playEnd") < media.get("playEnd")
        ) {
          right = media;
          const playStart = movedMedia.get("playEnd");

          if (playStart !== media.get("playStart")) {
            right = media.set("playStart", playStart);
            rightUpdater = rightUpdater.set("playStart", playStart);
          }

          const mediaStartTime =
            right.get(mediaEnd) -
            (right.get("playEnd") - right.get("playStart"));
          if (mediaStartTime !== right.get(mediaStart)) {
            right = right.set(mediaStart, mediaStartTime);
            rightUpdater = rightUpdater.set(mediaStart, mediaStartTime);
          }

          if (
            right.get("playEnd") - right.get("playStart") <
            timelineInterval
          ) {
            right = undefined;
          }
          isOverlapping = true;
        }

        if (isOverlapping) {
          if (left && right && leftUpdater && rightUpdater) {
            right = right.set("id", randomString("split"));
            right = right.set("dropId", right.get("id"));
            if (media.get("isReframe")) {
              const origReframeId = media.get("reframeOriginalId") || media.get("id");
              right = right.set("reframeOriginalId", origReframeId);
            }
            newMedia = newMedia
              .set(left.get("id"), left)
              .set(right.get("id"), right);
            currentTrackMediaIds.push(right.get("id"));

            if (leftUpdater.size > 0) {
              updaterObject = updaterObject.setIn(
                [container, left.get("id"), "data"],
                leftUpdater
              );
            }
            updaterObject = updaterObject.setIn(
              [container, right.get("id"), "isNew"],
              true
            );
            updaterObject = updaterObject.setIn(
              [container, right.get("id"), "isSplit"],
              true
            );
            updaterObject = updaterObject.setIn(
              [container, right.get("id"), "data"],
              right
            );
          } else if (!left && !right) {
            newMedia = newMedia.delete(media.get("id"));
            updaterObject = updaterObject.setIn(
              [container, media.get("id"), "isDelete"],
              true
            );
          } else if ((left && leftUpdater) || (right && rightUpdater)) {
            let updatedItem = left;
            let updater = leftUpdater;

            if (right && rightUpdater) {
              updatedItem = right;
              updater = rightUpdater;
            }

            newMedia = newMedia.set(updatedItem.get("id"), updatedItem);
            if (updater.size > 0) {
              updaterObject = updaterObject.setIn(
                [container, updatedItem.get("id"), "data"],
                updater
              );
            }
          }
        }
      }
    }
  }

  return {
    newMedia,
    updaterObject,
  };
};

/**
 * Use this function to remove empty tracks if an item's track is changed
 * @param {RemoveEmptyTracksParams} params
 */
// const removeEmptyTracks = (params = {}) => {
//   const { items, container } = params;
//   let { updaterObject } = params;

//   let trackToAssign = 0;
//   let prevTrack = null;
//   const sortedItems = items
//     .sortBy((item) => item.get("track"))
//     .map((item) => {
//       if (prevTrack === null) {
//         // first item
//         prevTrack = item.get("track");
//       }
//       if (prevTrack !== item.get("track")) {
//         trackToAssign = trackToAssign + 1;
//         prevTrack = item.get("track");
//       }
//       if (trackToAssign !== item.get("track")) {
//         item = item.set("track", trackToAssign);
//         updaterObject = updaterObject.setIn(
//           [container, item.get("id"), "data", "track"],
//           trackToAssign
//         );
//       }
//       return item;
//     });

//   return {
//     items: sortedItems,
//     updaterObject,
//   };
// };

/**
 * function to get duration of project by traversing all items in project
 * @param {GetProjectDurationParams} params
 */
export const getProjectDuration = (params = {}) => {
  const { project } = params;
  let duration = 0;

  for (const item of project.get("workspaceItems").valueSeq()) {
    if (item.get("exitEnd") > duration) {
      duration = item.get("exitEnd");
    }
  }

  for (const mediaContainer of ["audios", "workspaceBG"]) {
    for (const media of project.get(mediaContainer).valueSeq()) {
      if (media.get("playEnd") > duration) {
        duration = media.get("playEnd");
      }
    }
  }

  if (duration === 0) {
    // if duration is 0 then probably no item is in the project, keep default duraiton
    duration = RULER_OPTIONS.projectMinDuration;
  }

  return duration;
};

/**
 * Function to find which track has enough space for given time
 * @param {object} params
 * @param {number} params.startTime
 * @param {number} params.endTime
 * @param {"workspaceItems" | "audios"} params.container
 * @param {object} params.projectDetails
 * @param {"last" | "first"} params.order whether to insert at first or last track
 */
const getDropTrack = (params) => {
  const {
    container,
    endTime,
    projectDetails,
    startTime,
    order = "last",
  } = params;

  const containerData = projectDetails.get(container);

  if (containerData.size === 0) {
    return {
      track: 0,
      isNewTrack: false,
    };
  }

  let startKey = "playStart";
  let endKey = "playEnd";
  if (container === "workspaceItems") {
    startKey = "enterStart";
    endKey = "exitEnd";
  }

  let track = null;

  const sortedItems = containerData.sortBy((item) => item.get(startKey));
  const tracks = sortedItems
    .groupBy((item) => item.get("track"))
    .sortBy((trackItems, trackIndex) => {
      trackIndex = Number(trackIndex);
      return trackIndex;
    });

  const trackKeySeq = tracks.keySeq();
  const firstTrackKey = trackKeySeq.first();
  const lastTrackKey = trackKeySeq.last();
  const trackKey = order === "last" ? lastTrackKey : firstTrackKey;
  const trackItems = tracks.get(trackKey);
  const lastItem = trackItems.last();
  let prevEnd = 0;

  if (lastItem && startTime >= lastItem.get(endKey)) {
    track = Number(trackKey);
  } else {
    for (const item of tracks.get(trackKey).valueSeq()) {
      const curStart = item.get(startKey);
      const curEnd = item.get(endKey);

      if (startTime >= prevEnd && endTime <= curStart) {
        track = Number(trackKey);
        break;
      }

      prevEnd = curEnd;
    }
  }

  let isNewTrack = false;
  if (track === null && order === "last") {
    track = Number(lastTrackKey) + 1;
    isNewTrack = true;
  } else if (track === null && order === "first") {
    track = Number(firstTrackKey) - 1;
    if (track < 0) {
      track = 0;
    }
    isNewTrack = true;
  }

  return {
    isNewTrack,
    track,
  };
};

/**
 * Function to library replace
 * @param {object} params
 * @param {object} params.item
 * @param {object} params.newItem
 * @param {boolean | undefined} params.isNoCrop
 * @param {number} params.timelineInterval
 */
const replaceWorkspaceItem = (params = {}) => {
  const { item, isNoCrop = false, timelineInterval } = params;
  let { newItem } = params;
  const isEnableCrop =
    !isNoCrop &&
    ((isVideoOnly(newItem.get("type"), newItem.get("subType")) &&
      isVideoOnly(item.get("type"), item.get("subType")) &&
      !newItem.get("isSingleClipFrame") &&
      !isUpImageSVG(item) &&
      !isUpImageSVG(newItem)) ||
      (isImageOnly(item.get("type"), item.get("subType")) &&
        isImageOnly(newItem.get("type"), newItem.get("subType")) &&
        !newItem.get("isSingleClipFrame") &&
        !isUpImageSVG(item) &&
        !isUpImageSVG(newItem)));

  const keysToCopy = [
    "enterStart",
    "exitEnd",
    "enterEnd",
    "exitStart",
    "enterEffectName",
    "exitEffectName",
    "angle",
    "flipPosition",
    "opacity",
    "track",
  ];
  for (const key of keysToCopy) {
    newItem = newItem.set(key, item.get(key));
  }

  if (isVideoOnly(newItem.get("type"), newItem.get("subType"))) {
    let itemDuration = newItem.get("exitEnd") - newItem.get("enterStart");
    const playDuration = newItem.get("videoEnd") - newItem.get("videoStart");

    if (playDuration < itemDuration) {
      newItem = newItem.set("exitEnd", newItem.get("enterStart") + playDuration);
    }

    itemDuration = newItem.get("exitEnd") - newItem.get("enterStart");

    if (playDuration > itemDuration) {
      newItem = newItem.set("videoEnd", newItem.get("videoStart") + itemDuration);
    }

    if (item.get("transitionEnterEffect")) {
      newItem = newItem.set("transitionEnterEffect", item.get("transitionEnterEffect"));
    }

    if (item.get("transitionExitEffect")) {
      newItem = newItem.set("transitionExitEffect", item.get("transitionExitEffect"));
    }

    if (item.get("transitionEnterId")) {
      newItem = newItem.set("transitionEnterId", item.get("transitionEnterId"));
    }

    if (item.get("transitionExitId")) {
      newItem = newItem.set("transitionExitId", item.get("transitionExitId"));
    }

    if (item.get("transitionDuration")) {
      newItem = newItem.set("transitionDuration", item.get("transitionDuration"));
    }

    if (item.get("isTransition")) {
      newItem = newItem.set("isTransition", item.get("isTransition"));
    }

    if (item.get("speed")) {
      newItem = newItem.set("speed", item.get("speed"));
    }

    if (!newItem.get("isSingleClipFrame")) {
      newItem = newItem.set("volume", item.get("volume"));
    }
  }

  if (isEnableCrop) {
    const dropItem = {
      libAssetWidth: newItem.get("width"),
      libAssetHeight: newItem.get("height"),
    };
    const targetPlot = {
      x: item.get("x"),
      y: item.get("y"),
      width: item.get("width"),
      height: item.get("height"),
    };
    const { drop_item_original, item_plot } = applyCropToDropItem(dropItem, targetPlot);

    newItem = newItem.set("x", item_plot.x);
    newItem = newItem.set("y", item_plot.y);
    newItem = newItem.set("width", item_plot.w);
    newItem = newItem.set("height", item_plot.h);
    newItem = newItem.set("original", fromJS(drop_item_original));
    newItem = newItem.set("isCropped", true);
  }

  if (item.get("radius")) {
    newItem = newItem.set("radius", item.get("radius"));
  }

  if (item.get("filter") && isImageOnly(newItem.get("type"), newItem.get("subType"))) {
    newItem = newItem.set("filter", item.get("filter"));
  }

  if (newItem.get("exitEffectName") && newItem.get("exitEffectName") !== "no_Effect") {
    if (newItem.get("exitEnd") - newItem.get("exitStart") < timelineInterval) {
      newItem = newItem.set("exitEffectName", "no_Effect").set("exitStart", 0);
    }
    if ((isPropertyOnly(item.get("type"))) && isImageOnly(newItem.get("type"), newItem.get("subType"))) {
      newItem = newItem.set("exitEffectName", "no_Effect");
    }
  }

  if (newItem.get("enterEffectName") && newItem.get("enterEffectName") !== "no_Effect") {
    if ((isPropertyOnly(item.get("type"))) && isImageOnly(newItem.get("type"), newItem.get("subType"))) {
      newItem = newItem.set("enterEffectName", "no_Effect");
    }
    if (newItem.get("enterEnd") > newItem.get("exitEnd")) {
      newItem = newItem.set("enterEnd", newItem.get("exitEnd"));
    }
    if (newItem.get("enterEnd") - newItem.get("enterStart") < timelineInterval) {
      newItem = newItem.set("enterEffectName", "no_Effect").set("enterEnd", 0);
    }
  }

  return newItem;
};

/**
 * Function to replace audio.
 * @param {item: object, newItem: object} params
 * @returns newItem object
 */
const replaceAudioItem = (params = {}) => {
  const { item } = params;
  let { newItem } = params;

  const keysToCopy = [
    "playStart",
    "playEnd",
    "volume",
    "fadeDetails",
    "track",
  ];
  for (const key of keysToCopy) {
    newItem = newItem.set(key, item.get(key));
  }

  let itemDuration = newItem.get("playEnd") - newItem.get("playStart");
  const playDuration = newItem.get("musicEnd") - newItem.get("musicStart");

  if (playDuration < itemDuration) {
    newItem = newItem.set("playEnd", newItem.get("playStart") + playDuration);
  }

  itemDuration = newItem.get("playEnd") - newItem.get("playStart");

  if (playDuration > itemDuration) {
    newItem = newItem.set("musicEnd", newItem.get("musicStart") + itemDuration);
  }

  return newItem;
}

/**
 * Function to handle autosave for timeline trim/move
 * @param {object} params
 * @param {object} params.action action object of timeline update dispatch
 * @param {object} params.projectDetails project state from redux
 * @param {number} params.playhead playhead time from redux
 * @param {number} params.willEmit whether returned object will be sent through socket
 * @param {number} params.timelineInterval duration of each step in timeline in seconds
 * @returns {object}
 */
export const getTimelineUpdater = (params = {}) => {
  const { action, projectDetails, playhead, willEmit, timelineInterval, reframeData, appReframeData } = params;
  const { payload } = action;

  // Sync Reframe animation duration / effect updates across all related scenes and parent
  if (payload && Array.isArray(payload.toUpdate)) {
    const workspaceItems = projectDetails.get("workspaceItems");
    if (workspaceItems) {
      const extraUpdates = [];
      payload.toUpdate.forEach((itemUpdate) => {
        if (itemUpdate.container === "workspaceItems" && itemUpdate.toUpdate) {
          const currentItem = workspaceItems.get(itemUpdate.id);
          if (currentItem) {
            const isReframeParent = currentItem.get("isReframe") === true;
            const originalId = currentItem.get("originalId") || currentItem.get("dropId");
            const isReframeChild = !!currentItem.get("sceneData") && !!originalId;

            if (isReframeParent || isReframeChild) {
              const parentId = isReframeParent ? itemUpdate.id : originalId;

              // Find what animation values are being updated
              const syncedValues = {};
              if (itemUpdate.toUpdate.enterEnd !== undefined) {
                syncedValues.enterEnd = itemUpdate.toUpdate.enterEnd;
              }
              if (itemUpdate.toUpdate.exitStart !== undefined) {
                syncedValues.exitStart = itemUpdate.toUpdate.exitStart;
              }
              if (itemUpdate.toUpdate.enterEffectName !== undefined) {
                syncedValues.enterEffectName = itemUpdate.toUpdate.enterEffectName;
              }
              if (itemUpdate.toUpdate.exitEffectName !== undefined) {
                syncedValues.exitEffectName = itemUpdate.toUpdate.exitEffectName;
              }

              // If any of these animation properties are changing, sync them!
              const isSplitAction = payload.toUpdate.some(u => u.isSplit);
              if (Object.keys(syncedValues).length > 0 && !isSplitAction) {
                // Find all related items (parent + child scenes)
                workspaceItems.forEach((wsItem) => {
                  const wsItemId = wsItem.get("id");
                  if (wsItemId === itemUpdate.id) return; // already in toUpdate

                  const wsOriginalId = wsItem.get("originalId") || wsItem.get("dropId");
                  if (
                    wsItemId === parentId ||
                    (wsItem.get("sceneData") && wsOriginalId === parentId)
                  ) {
                    // Check if there is already an update in payload.toUpdate for this related item
                    const existing = payload.toUpdate.find(u => u.container === "workspaceItems" && u.id === wsItemId);
                    if (existing) {
                      existing.toUpdate = { ...existing.toUpdate, ...syncedValues };
                    } else {
                      extraUpdates.push({
                        container: "workspaceItems",
                        id: wsItemId,
                        toUpdate: { ...syncedValues },
                      });
                    }
                  }
                });
              }
            }
          }
        }
      });
      if (extraUpdates.length > 0) {
        payload.toUpdate = [...payload.toUpdate, ...extraUpdates];
      }
    }
  }

  let newProjectDetails = projectDetails;

  let mayItemsOverlap = false;
  const checkItemEmptyTracks = false;
  const movedItemIds = [];

  let mayAudiosOverlap = false;
  const checkAudioEmptyTracks = false;
  const movedAudioIds = [];

  let mayVideosOverlap = false;
  const movedVideoIds = [];

  const newTracks = {};

  let removedMediaDropIds = fromJS({});
  let subtitleItemDeleteDropIds = ImmutableSet();

  let subtitleList = null;

  let updaterObject = fromJS({});
  payload.toUpdate.forEach((itemUpdate) => {
    if (itemUpdate.container === "subtitleGlobal") {
      Object.entries(itemUpdate.toUpdate).forEach(([key, value]) => {
        let subtitleStyleData = newProjectDetails.getIn(["subtitle", key]);

        if (typeof value === "object" && value !== null && !Array.isArray(value)) {
          // If value is an object → merge
          subtitleStyleData = subtitleStyleData
            ? subtitleStyleData.merge(fromJS(value))
            : fromJS(value);
        } else {
          // If value is primitive (like string) → just assign
          subtitleStyleData = fromJS(value);
        }

        updaterObject = updaterObject.setIn(
          ["subtitle", itemUpdate.container, key],
          subtitleStyleData
        );
      });

    } else if (itemUpdate.container === "subtitleDropData") {
      if (itemUpdate.isAdd) {
        const newDropData = fromJS(itemUpdate.newItemData);

        newProjectDetails = newProjectDetails.setIn(
          ["subtitle", "data", itemUpdate.dropId],
          newDropData
        );

        const subtitleUpdater = fromJS({
          isNew: true,
          data: newDropData,
        });

        updaterObject = updaterObject.setIn(
          [
            "subtitle",
            itemUpdate.container,
            itemUpdate.dropId,
          ],
          subtitleUpdater
        );
      } else if (itemUpdate.isDelete) {
        newProjectDetails = newProjectDetails.deleteIn([
          "subtitle",
          "data",
          itemUpdate.dropId,
        ]);
        const subtitleUpdater = fromJS({
          isDelete: true,
        });
        updaterObject = updaterObject.setIn(
          [
            "subtitle",
            itemUpdate.container,
            itemUpdate.dropId,
          ],
          subtitleUpdater
        );
      } else {
        // if you ever need to implement this else case, you probably need to use `subtitleData` container to add/remove items, not this container
      }
    } else if (itemUpdate.container === "subtitleData") {
      if (itemUpdate.isAdd) {
        const newItem = fromJS(itemUpdate.newItemData);

        newProjectDetails = newProjectDetails.setIn(
          ["subtitle", "data", itemUpdate.dropId, itemUpdate.id],
          newItem
        );

        let subtitleUpdater = fromJS({
          timelineId: itemUpdate.timelineId,
          isNew: true,
          data: newItem,
        });
        if (itemUpdate.isSplit) {
          subtitleUpdater = subtitleUpdater.set("isSplit", true);
        }
        if (itemUpdate.isReplace) {
          subtitleUpdater = subtitleUpdater.set("isReplace", true);
        }

        updaterObject = updaterObject.setIn(
          [
            "subtitle",
            itemUpdate.container,
            itemUpdate.dropId,
            itemUpdate.id,
          ],
          subtitleUpdater
        );

        // Force-register brand-new subtitle containers immediately when first subtitle item
        // is added under a new dropId. This avoids backend dropping subtitleData updates for
        // unknown containers during split flows.
        const isOldSubtitleDrop = projectDetails.hasIn(["subtitle", "data", itemUpdate.dropId]);
        const existingDropUpdater = updaterObject.getIn(["subtitle", "subtitleDropData", itemUpdate.dropId]);
        const existingDropMarkedDelete = existingDropUpdater && existingDropUpdater.get("isDelete") === true;
        if (!isOldSubtitleDrop && (!existingDropUpdater || existingDropMarkedDelete)) {
          const latestDropData = newProjectDetails.getIn(["subtitle", "data", itemUpdate.dropId]) || fromJS({});
          updaterObject = updaterObject.setIn(
            ["subtitle", "subtitleDropData", itemUpdate.dropId],
            fromJS({
              isNew: true,
              data: latestDropData,
            })
          );
        }
      } else if (itemUpdate.isDelete) {
        newProjectDetails = newProjectDetails.deleteIn([
          "subtitle",
          "data",
          itemUpdate.dropId,
          itemUpdate.id,
        ]);
        const subtitleUpdater = fromJS({
          timelineId: itemUpdate.timelineId,
          isDelete: true,
        });
        updaterObject = updaterObject.setIn(
          [
            "subtitle",
            itemUpdate.container,
            itemUpdate.dropId,
            itemUpdate.id,
          ],
          subtitleUpdater
        );
        subtitleItemDeleteDropIds = subtitleItemDeleteDropIds.add(itemUpdate.dropId);
      } else {
        let item = newProjectDetails.getIn([
          "subtitle",
          "data",
          itemUpdate.dropId,
          itemUpdate.id,
        ]);
        let subtitleUpdater = fromJS({
          timelineId: itemUpdate.timelineId,
          data: {},
        });

        Reflect.ownKeys(itemUpdate.toUpdate).forEach((updateKey) => {
          const updateVal = itemUpdate.toUpdate[updateKey];
          item = item.set(updateKey, updateVal);

          subtitleUpdater = subtitleUpdater.setIn(
            ["data", updateKey],
            updateVal
          );
        });

        if (subtitleUpdater.get("data").size) {
          updaterObject = updaterObject.setIn(
            [
              "subtitle",
              itemUpdate.container,
              itemUpdate.dropId,
              itemUpdate.id,
            ],
            subtitleUpdater
          );
        }

        newProjectDetails = newProjectDetails.setIn(
          [itemUpdate.container, "data", itemUpdate.dropId, itemUpdate.id],
          item
        );
      }
    }
    else if (itemUpdate.container === "zoomData") {
      if (itemUpdate.isDelete) {
        // Delete zoomData from workspace item
        newProjectDetails = newProjectDetails.deleteIn([
          "workspaceItems",
          itemUpdate.itemId,
          "zoomData"
        ]);

        // Add updaterObject entry to delete zoomData from workspace items
        updaterObject = updaterObject.setIn(
          ["workspaceItems", itemUpdate.itemId, "data", "zoomData"],
          fromJS({})
        );

        // Delete all localZoom entries for this workspace item
        const localZoom = newProjectDetails.get("localZoom");
        if (localZoom) {
          const keysToDelete = localZoom
            .keySeq()
            .filter(key => key.startsWith(`${itemUpdate.itemId}-`))
            .toArray();

          keysToDelete.forEach(key => {
            updaterObject = updaterObject.deleteIn(["localZoom", key]);
          });
        }
      }
    } else if (itemUpdate.container === "subtitleList") {
      if (!subtitleList) {
        subtitleList = newProjectDetails.get("subtitleData");
      }
      if (itemUpdate.isAdd) {
        subtitleList = subtitleList.push(fromJS(itemUpdate.data));
      } else if (itemUpdate.isDelete) {
        subtitleList = subtitleList.filter(
          (entry) => entry.get("subtitleId") !== itemUpdate.id
        );
      } else {
        const index = subtitleList.findIndex(
          (entry) => entry.get("subtitleId") === itemUpdate.id
        );
        if (index >= 0) {
          const entry = subtitleList.get(index).merge(fromJS(itemUpdate.data));
          subtitleList = subtitleList.set(index, entry);
        }
      }
      newProjectDetails = newProjectDetails.set("subtitleData", subtitleList);
    } else if (itemUpdate.isAdd) {
      let newItem = fromJS(itemUpdate.newItemData);

      if (itemUpdate.isLibraryDrop || itemUpdate.isAudioDetach) {
        let startTime = 0;
        let endTime = 0;

        if (itemUpdate.isAudioDetach) {
          startTime = newItem.get("playStart");
          endTime = newItem.get("playEnd");
        } else if (itemUpdate.container === "workspaceItems") {
          if (!itemUpdate.isReframe) {
            newItem = newItem.set("enterStart", playhead);
          }

          let duration;
          // let duration = projectDetails.get("duration") - playhead;
          if (isVideoOnly(newItem.get("type"), newItem.get("subType"))) {
            duration = newItem.get("videoDuration");
          } else if (isAudioOnly(newItem.get("type"))) {
            duration = newItem.get("musicDuration");
          } else {
            duration = DEFAULT_DURATION;
          }
          if (!itemUpdate.isReframe) {
            newItem = newItem.set("exitEnd", playhead + duration);
            newItem = newItem.set("enterEnd", 0);
            newItem = newItem.set("exitStart", 0);
            newItem = newItem.set("enterEffectName", "no_Effect");
            newItem = newItem.set("exitEffectName", "no_Effect");
          }
          startTime = newItem.get("enterStart");
          endTime = newItem.get("exitEnd");
        } else {
          if (!itemUpdate.isReframe) {
            newItem = newItem.set("playStart", playhead);
            newItem = newItem.set(
              "playEnd",
              playhead + newItem.get("musicDuration")
            );
          }
          startTime = newItem.get("playStart");
          endTime = newItem.get("playEnd");
        }

        // All workspace items now use "last" so they receive the highest track index (which is mapped to the top layer).
        const dropOrder = "last";

        const { track, isNewTrack } = getDropTrack({
          container: itemUpdate.container,
          startTime,
          endTime,
          order: dropOrder,
          projectDetails,
        });
        if (!itemUpdate.isReframe) {
          newItem = newItem.set("track", track);
        }

        if (
          isNewTrack
          && (
            itemUpdate.container === "workspaceItems"
            || itemUpdate.container === "audios"
          )
        ) {
          if (!newTracks[itemUpdate.container]) {
            newTracks[itemUpdate.container] = {};
          }
          if (!newTracks[itemUpdate.container][track]) {
            newTracks[itemUpdate.container][track] = {
              trackIndex: track,
              ids: [],
            };
          }
          newTracks[itemUpdate.container][track].ids.push(
            itemUpdate.id
          );
        }
      } else if (itemUpdate.isWorkspaceReplace) {
        let prevItem = newProjectDetails.getIn([
          itemUpdate.container,
          itemUpdate.id,
        ]);
        if (itemUpdate.swapDetails) {
          prevItem = itemUpdate.swapDetails;
        }
        if (itemUpdate.container === "audios") {
          newItem = replaceAudioItem({ item: prevItem, newItem });
        } else {
          newItem = replaceWorkspaceItem({ item: prevItem, newItem, isNoCrop: itemUpdate.isNoCrop, timelineInterval, });
        }

        const prevType = prevItem.get("type");
        const prevSubType = prevItem.get("subType");
        const prevDropId = prevItem.get("dropId");
        if (isVideoOnly(prevType, prevSubType) || isAudioOnly(prevType, prevSubType)) {
          removedMediaDropIds = removedMediaDropIds.setIn([itemUpdate.container, prevDropId], true);
        }

        if (isVideoOnly(prevType, prevSubType) && !isVideoOnly(newItem.get("type"), newItem.get("subType"))) {
          if (prevItem.get("transitionExitId") !== "none" && prevItem.get("transitionExitId") !== undefined) {
            updaterObject = updaterObject.setIn(
              [itemUpdate.container, prevItem.get("transitionExitId"), "data", "transitionEnterId"],
              "none"
            );
            newItem = newItem.set("transitionExitId", "none");
          }
          if (prevItem.get("transitionEnterId") !== "none" && prevItem.get("transitionEnterId") !== undefined) {
            updaterObject = updaterObject.setIn(
              [itemUpdate.container, prevItem.get("transitionEnterId"), "data", "transitionExitId"],
              "none"
            );
            newItem = newItem.set("transitionEnterId", "none");
          }
        }
      } else if (itemUpdate.isNewTrack && (itemUpdate.container === "workspaceItems" || itemUpdate.container === "audios")) {
        const track = newItem.get("track");
        if (!newTracks[itemUpdate.container]) {
          newTracks[itemUpdate.container] = {};
        }
        if (!newTracks[itemUpdate.container][track]) {
          newTracks[itemUpdate.container][track] = {
            trackIndex: track,
            ids: [],
          };
        }
        newTracks[itemUpdate.container][track].ids.push(itemUpdate.id);
      }

      newProjectDetails = newProjectDetails.setIn(
        [itemUpdate.container, newItem.get("id")],
        newItem
      );
      updaterObject = updaterObject.setIn(
        [itemUpdate.container, newItem.get("id"), "isNew"],
        true
      );
      updaterObject = updaterObject.setIn(
        [itemUpdate.container, newItem.get("id"), "data"],
        newItem
      );
      if (itemUpdate.isWorkspaceReplace) {
        updaterObject = updaterObject.setIn(
          [itemUpdate.container, newItem.get("id"), "isReplace"],
          true
        );
      }
      if (itemUpdate.isSplit) {
        updaterObject = updaterObject.setIn(
          [itemUpdate.container, newItem.get("id"), "isSplit"],
          true
        );
      }
      if (itemUpdate.isDontSelect) {
        updaterObject = updaterObject.setIn(
          [itemUpdate.container, newItem.get("id"), "isDontSelect"],
          true
        );
      }

      if (itemUpdate.container === "workspaceBG") {
        movedVideoIds.push(newItem.get("id"));
      } else if (itemUpdate.container === "workspaceItems") {
        movedItemIds.push(newItem.get("id"));
      } else if (itemUpdate.container === "audios") {
        movedAudioIds.push(newItem.get("id"));
      }
    } else if (itemUpdate.isDelete) {
      const item = newProjectDetails.getIn([
        itemUpdate.container,
        itemUpdate.id,
      ]);
      // deletion of workspace
      let isReframeDrop = false;
      if (itemUpdate.container === "workspaceItems") {
        isReframeDrop = item ? isReframeDropItem(item) : false;
        if (isReframeDrop) {
          const droppedId = item.get("dropId");
          const currentWsItems = newProjectDetails.get("workspaceItems");
          if (currentWsItems) {
            currentWsItems.forEach((wsItem, wsKey) => {
              if (wsItem && wsItem.get("dropId") === droppedId && wsKey !== itemUpdate.id && !wsItem.get("sceneData")) {
                newProjectDetails = newProjectDetails.deleteIn(["workspaceItems", wsKey]);
                updaterObject = updaterObject.setIn(["workspaceItems", wsKey, "isDelete"], true);
              }
            });
          }
        }

        // Also cascade-delete scene items whose originalId matches the deleted item's id
        // This must run for both reframe drop items and regular items so that all AI reframe
        // scene clips are removed when the original video is deleted.
        {
          const deletedItemId = itemUpdate.id;
          const currentWsItems = newProjectDetails.get("workspaceItems");
          if (currentWsItems) {
            currentWsItems.forEach((wsItem, wsKey) => {
              if (wsItem && wsItem.get("originalId") === deletedItemId && wsItem.get("sceneData")) {
                newProjectDetails = newProjectDetails.deleteIn(["workspaceItems", wsKey]);
                updaterObject = updaterObject.setIn(["workspaceItems", wsKey, "isDelete"], true);
              }
            });
          }
        }

        const isVideo = item && isVideoOnly(item.get("type"), item.get("subType"));
        if (isVideo && !item.get("sceneData")) {
          const deletedDropId = item.get("dropId");
          const deletedSubMap = newProjectDetails.getIn(["subtitle", "data", deletedDropId]);

          const deletedAssetId = item.get("assetId");
          const deletedSrc = item.getIn(["assetData", "src"]) || item.get("src");
          const deletedFamilyId = item.get("originalId") || itemUpdate.id;
          const currentContainerItems = newProjectDetails.get(itemUpdate.container);

          // Find a surviving sibling in the same family
          let survivingSibling = null;
          if (currentContainerItems) {
            currentContainerItems.forEach((wsItem, wsKey) => {
              if (survivingSibling) return; // already found one
              if (wsKey === itemUpdate.id) return; // skip self
              if (!wsItem || wsItem.get("sceneData")) return; // skip scene children
              if (!isVideoOnly(wsItem.get("type"), wsItem.get("subType"))) return;

              const wsFamilyId = wsItem.get("originalId") || wsKey;
              const wsAssetId = wsItem.get("assetId");
              const wsSrc = wsItem.getIn(["assetData", "src"]) || wsItem.get("src");

              const isSameFamily =
                (item.get("originalId") && wsFamilyId === item.get("originalId")) ||
                (wsFamilyId === deletedFamilyId || wsKey === deletedFamilyId) ||
                (deletedAssetId && wsAssetId === deletedAssetId) ||
                (deletedSrc && (wsSrc === deletedSrc));

              if (isSameFamily) {
                survivingSibling = { itemId: wsKey, dropId: wsItem.get("dropId") };
              }
            });
          }

          if (deletedSubMap && deletedSubMap.size > 0) {
            if (survivingSibling) {
              deletedSubMap.forEach((subItem, subId) => {
                // Move subtitle to surviving sibling's dropId
                newProjectDetails = newProjectDetails.setIn(
                  ["subtitle", "data", survivingSibling.dropId, subId],
                  subItem
                );

                updaterObject = updaterObject.setIn(
                  ["subtitle", "subtitleData", survivingSibling.dropId, subId],
                  fromJS({
                    timelineId: `${survivingSibling.itemId}-${survivingSibling.dropId}-${subId}`,
                    isNew: true,
                    data: subItem,
                  })
                );

                // Delete from the old dropId
                newProjectDetails = newProjectDetails.deleteIn([
                  "subtitle", "data", deletedDropId, subId,
                ]);

                updaterObject = updaterObject.setIn(
                  ["subtitle", "subtitleData", deletedDropId, subId],
                  fromJS({
                    timelineId: `${itemUpdate.id}-${deletedDropId}-${subId}`,
                    isDelete: true,
                  })
                );
              });
              subtitleItemDeleteDropIds = subtitleItemDeleteDropIds.add(deletedDropId);
            } else {
              // No surviving sibling, delete the subtitles for this dropId
              deletedSubMap.forEach((subItem, subId) => {
                newProjectDetails = newProjectDetails.deleteIn([
                  "subtitle", "data", deletedDropId, subId,
                ]);

                updaterObject = updaterObject.setIn(
                  ["subtitle", "subtitleData", deletedDropId, subId],
                  fromJS({
                    timelineId: `${itemUpdate.id}-${deletedDropId}-${subId}`,
                    isDelete: true,
                  })
                );
              });
              subtitleItemDeleteDropIds = subtitleItemDeleteDropIds.add(deletedDropId);
            }
          }

          // Clean up assetDetails in subtitleList
          if (!subtitleList) {
            subtitleList = newProjectDetails.get("subtitleData");
          }
          if (subtitleList) {
            subtitleList = subtitleList.map(subEntry => {
              const assetDetails = subEntry.get("assetDetails");
              if (assetDetails) {
                const index = assetDetails.findIndex(asset => asset.get("itemId") === deletedDropId);
                if (index !== -1) {
                  let newAssetDetails;
                  if (survivingSibling) {
                    const existingSiblingIndex = assetDetails.findIndex(asset => asset.get("itemId") === survivingSibling.dropId);
                    if (existingSiblingIndex === -1) {
                      newAssetDetails = assetDetails.set(index, fromJS({
                        assetId: assetDetails.getIn([index, "assetId"]),
                        itemId: survivingSibling.dropId
                      }));
                    } else {
                      newAssetDetails = assetDetails.delete(index);
                    }
                  } else {
                    newAssetDetails = assetDetails.delete(index);
                  }
                  return subEntry.set("assetDetails", newAssetDetails);
                }
              }
              return subEntry;
            });
            newProjectDetails = newProjectDetails.set("subtitleData", subtitleList);
          }
        }
      }
      newProjectDetails = newProjectDetails.deleteIn([
        itemUpdate.container,
        itemUpdate.id,
      ]);

      if (item && isVideoOnly(item.get("type"), item.get("subType"))) {
        //  Set transitionExitId or transitionEnterId only if it is an existing item
        if (item.get("transitionExitId") !== "none" && item.get("transitionExitId") !== undefined) {
          const data = newProjectDetails.getIn([itemUpdate.container, item.get("transitionExitId")]);
          if (data) {
            updaterObject = updaterObject.setIn(
              [itemUpdate.container, item.get("transitionExitId"), "data", "transitionEnterId"],
              "none"
            );

            newProjectDetails = newProjectDetails.setIn([
              itemUpdate.container,
              item.get("transitionExitId"),
              "transitionEnterId"
            ], "none");
          }
        }
        if (item.get("transitionEnterId") !== "none" && item.get("transitionEnterId") !== undefined) {
          const data = newProjectDetails.getIn([itemUpdate.container, item.get("transitionEnterId")]);
          if (data) {
            updaterObject = updaterObject.setIn(
              [itemUpdate.container, item.get("transitionEnterId"), "data", "transitionExitId"],
              "none"
            );
            newProjectDetails = newProjectDetails.setIn([
              itemUpdate.container,
              item.get("transitionEnterId"),
              "transitionExitId"
            ], "none");
          }
        }
      }

      updaterObject = updaterObject.setIn(
        [itemUpdate.container, itemUpdate.id, "isDelete"],
        true
      );
      if (isReframeDrop) {
        updaterObject = updaterObject.setIn(
          [itemUpdate.container, itemUpdate.id, "isReframeDropItem"],
          true
        );
      }
      // Prevent renumbering tracks after delete-only actions.
      // Cascade delete of scene items should not change the existing track priority.

      const type = item.get("type");
      const subType = item.get("subType");
      const dropId = item.get("dropId");
      if (isVideoOnly(type, subType) || isAudioOnly(type, subType)) {
        removedMediaDropIds = removedMediaDropIds.setIn([itemUpdate.container, dropId], true);
      }
    } else if (itemUpdate.isObjToVid || itemUpdate.isVidToObj) {
      let newItem = fromJS(itemUpdate.newItemData);
      const item = newProjectDetails.getIn([
        itemUpdate.fromContainer,
        itemUpdate.id,
      ]);

      newItem = newItem
        .set("id", item.get("id"))
        .set("assetId", item.get("assetId"))
        .set("type", item.get("type"))
        .set("subType", item.get("subType"))
        .set("src", item.get("src"))
        .set("thumb", item.get("thumb"))
        .set("x", item.get("x"))
        .set("y", item.get("y"))
        .set("width", item.get("width"))
        .set("height", item.get("height"))
        .set("pwidth", item.get("pwidth"))
        .set("pheight", item.get("pheight"))
        .set("angle", 0)
        .set("flipPosition", item.get("flipPosition"))
        .set("filter", item.get("filter"))
        .set("volume", item.get("volume"))
        .set("stickerify", item.get("stickerify"))
        .set("strokeOptions", item.get("strokeOptions"))
        .set("firstEdit", item.get("firstEdit"))
        .set("isTransparent", item.get("isTransparent"))
        .set("stickerifyPath", item.get("stickerifyPath"))
        .set("originalPath", item.get("originalPath"))
        .set("recentEdit", item.get("recentEdit"))
        .set("bgremoval", item.get("bgremoval"))
        .set("bgremovalPath", item.get("bgremovalPath"))
        .set("sourceType", item.get("sourceType"))
        .set("isBlob", item.get("isBlob"));

      if (newItem.get("isBlob")) {
        newItem = newItem.set("thumb", "");
      }

      if (itemUpdate.isObjToVid) {
        const workspaceWidth = newProjectDetails.get("width");
        const workspaceHeight = newProjectDetails.get("height");

        let bgWidth = newItem.get("pwidth");
        let bgHeight = newItem.get("pheight");

        if (!Number.isFinite(bgWidth)) {
          bgWidth = newItem.get("width");
          if (newItem.get("isCropped") && newItem.get("original")) {
            bgWidth =
              newItem.get("width") * newItem.getIn(["original", "width"]);
          }
        }
        if (!Number.isFinite(bgHeight)) {
          bgHeight = newItem.get("height");
          if (newItem.get("isCropped") && newItem.get("original")) {
            bgHeight =
              newItem.get("height") * newItem.getIn(["original", "height"]);
          }
        }

        // first try to fit width
        let scaledWidth = workspaceWidth;
        let scaledHeight = (bgHeight / bgWidth) * scaledWidth;

        // if there is gap, fit height
        if (scaledHeight < workspaceHeight) {
          scaledHeight = workspaceHeight;
          scaledWidth = (bgWidth / bgHeight) * scaledHeight;
        }

        const scaledCenter = { x: scaledWidth / 2, y: scaledHeight / 2 };
        const workspaceCenter = {
          x: workspaceWidth / 2,
          y: workspaceHeight / 2,
        };

        const x = workspaceCenter.x - scaledCenter.x;
        const y = workspaceCenter.y - scaledCenter.y;

        newItem = newItem
          .set("x", x / workspaceWidth)
          .set("y", y / workspaceHeight)
          .set("width", scaledWidth / workspaceWidth)
          .set("height", scaledHeight / workspaceHeight);
      } else if (itemUpdate.isVidToObj) {
        const workspaceWidth = newProjectDetails.get("width");
        const workspaceHeight = newProjectDetails.get("height");

        const scaledWidth = item.get("width") * workspaceWidth;
        const scaledHeight = item.get("height") * workspaceHeight;

        let dropWidth = scaledWidth;
        let dropHeight = scaledHeight;

        if (scaledWidth > scaledHeight) {
          dropWidth = workspaceWidth / 2;
          dropHeight = scaledHeight * (dropWidth / scaledWidth);
        } else {
          dropHeight = workspaceWidth / 2;
          dropWidth = scaledWidth * (dropHeight / scaledHeight);
        }

        const x = workspaceWidth / 2 - dropWidth / 2;
        const y = workspaceHeight / 2 - dropHeight / 2;

        newItem = newItem
          .set("opacity", 1)
          .set("x", x)
          .set("y", y)
          .set("width", dropWidth)
          .set("height", dropHeight);
      }

      const sourceContainerData = newProjectDetails
        .get(itemUpdate.fromContainer)
        .remove(itemUpdate.id);
      newProjectDetails = newProjectDetails.set(
        itemUpdate.fromContainer,
        sourceContainerData
      );
      updaterObject = updaterObject.setIn(
        [itemUpdate.fromContainer, itemUpdate.id, "isDelete"],
        true
      );

      newProjectDetails = newProjectDetails.setIn(
        [itemUpdate.container, newItem.get("id")],
        newItem
      );
      updaterObject = updaterObject.setIn(
        [itemUpdate.container, newItem.get("id"), "isNew"],
        true
      );
      updaterObject = updaterObject.setIn(
        [itemUpdate.container, newItem.get("id"), "data"],
        newItem
      );

      if (itemUpdate.isObjToVid) {
        mayVideosOverlap = true;
        movedVideoIds.push(newItem.get("id"));
      } else if (itemUpdate.isVidToObj) {
        if (itemUpdate.isNewTrack) {
          if (!newTracks[itemUpdate.container]) {
            newTracks[itemUpdate.container] = {};
          }
          if (!newTracks[itemUpdate.container][newItem.get("track")]) {
            newTracks[itemUpdate.container][newItem.get("track")] = {
              trackIndex: newItem.get("track"),
              ids: [],
            };
          }
          newTracks[itemUpdate.container][newItem.get("track")].ids.push(
            newItem.id
          );
        } else {
          movedItemIds.push(newItem.get("id"));
        }
      }
      mayItemsOverlap = true;
    } else if (
      itemUpdate.container === "workspaceItems" ||
      itemUpdate.container === "workspaceChildren" || // for workspace item -> frame child drag and drop
      itemUpdate.container === "audios" ||
      itemUpdate.container === "workspaceBG"
    ) {
      let item = newProjectDetails.getIn([itemUpdate.container, itemUpdate.id]);

      Reflect.ownKeys(itemUpdate.toUpdate).forEach((updateKey) => {
        let updateObj = itemUpdate.toUpdate[updateKey];
        let updatePath = [updateKey];
        let updateVal = updateObj;

        if (updateKey === "frameClipImage") {
          updatePath = ["clipDetails", updateObj.clipId, "imgDetails"];
          updateVal = updateObj.imgDetails;
          updateObj = fromJS(updateObj);
        }

        // Handle zoomData updates specifically
        if (updateKey === "start" || updateKey === "end" || updateKey === "disableZoomOut" || updateKey === "disableZoomIn") {
          const zoomId = itemUpdate.timelineId; // The zoom item ID
          const zoomUpdateValue = updateObj; // The start/end value                    
          // Special handling for zoomOut end time synchronization
          if (updateKey === "end") {
            const zoomDataContainer = item.getIn(["zoomData"]);
            if (zoomDataContainer) {
              zoomDataContainer.get(zoomId).entrySeq().forEach(([otherZoomId, zoomItem]) => {
                if (otherZoomId !== zoomId && zoomItem.get("type") === "zoomOut") {
                  // Update other zoom items to use the same end value when zoomOut is updated
                  const zoomOutDuration = zoomItem.get("end") - zoomItem.get("start");
                  const zoomOutEnd = zoomUpdateValue - zoomOutDuration;
                  item = item.setIn(["zoomData", zoomId, otherZoomId, "end"], zoomUpdateValue);
                  item = item.setIn(["zoomData", zoomId, otherZoomId, "start"], zoomOutEnd);
                }
              });
            }
          } else if (updateKey === "start") {
            const zoomDataContainer = item.getIn(["zoomData"]);
            if (zoomDataContainer) {
              zoomDataContainer.get(zoomId).entrySeq().forEach(([otherZoomId, zoomItem]) => {
                // let zoomInStartValue;
                if (otherZoomId !== zoomId && zoomItem.get("type") === "zoomIn") {
                  // Update other zoom items to use the same end value when zoomOut is updated
                  const zoomInDuration = zoomItem.get("end") - zoomItem.get("start");
                  const zoomEnterEnd = zoomUpdateValue + zoomInDuration;
                  item = item.setIn(["zoomData", zoomId, otherZoomId, "start"], zoomUpdateValue);
                  item = item.setIn(["zoomData", zoomId, otherZoomId, "end"], zoomEnterEnd);
                }
              });
            }
          } else if (updateKey === "disableZoomOut") {
            const zoomDataContainer = item.getIn(["zoomData"]);
            if (zoomDataContainer) {
              zoomDataContainer.get(zoomId).entrySeq().forEach(([otherZoomId, zoomItem]) => {
                // let zoomInStartValue;
                if (otherZoomId !== zoomId && zoomItem.get("type") === "zoomOut") {
                  // Update other zoom items to use the same end value when zoomOut is updated
                  item = item.setIn(["zoomData", zoomId, otherZoomId, "disableZoomOut"], zoomUpdateValue);
                }
              });
            }
          } else if (updateKey === "disableZoomIn") {
            const zoomDataContainer = item.getIn(["zoomData"]);
            if (zoomDataContainer) {
              zoomDataContainer.get(zoomId).entrySeq().forEach(([otherZoomId, zoomItem]) => {
                // let zoomInStartValue;
                if (otherZoomId !== zoomId && zoomItem.get("type") === "zoomOut") {
                  // Update other zoom items to use the same end value when zoomOut is updated
                  item = item.setIn(["zoomData", zoomId, otherZoomId, "disableZoomIn"], zoomUpdateValue);
                }
              });
            }
          }
          // Get the entire updated zoomData object
          const updatedZoomData = item.getIn(["zoomData"]);
          if (updatedZoomData) {
            // Update the updaterObject with the entire zoomData object
            updaterObject = updaterObject.setIn(
              ["workspaceItems", item.get("id"), "data", "zoomData"],
              updatedZoomData
            );
          }
        } else {
          // Regular property updates
          const isObj = typeof updateVal === "object" && updateVal !== null && !isImmutable(updateVal);
          item = item?.setIn(updatePath, isObj ? fromJS(updateVal) : updateVal);
          updaterObject = updaterObject.setIn(
            [itemUpdate.container, item.get("id"), "data", updateKey],
            updateObj
          );
        }

        if (itemUpdate.container === "workspaceItems") {
          mayItemsOverlap = true;
          movedItemIds.push(itemUpdate.id);
        } else if (itemUpdate.container === "audios") {
          mayAudiosOverlap = true;
          movedAudioIds.push(itemUpdate.id);
        } else if (itemUpdate.container === "workspaceBG") {
          mayVideosOverlap = true;
          movedVideoIds.push(itemUpdate.id);
        }
      });

      if (
        itemUpdate.isNewTrack &&
        (itemUpdate.container === "workspaceItems" ||
          itemUpdate.container === "audios")
      ) {
        if (!newTracks[itemUpdate.container]) {
          newTracks[itemUpdate.container] = {};
        }
        if (!newTracks[itemUpdate.container][item.get("track")]) {
          newTracks[itemUpdate.container][item.get("track")] = {
            trackIndex: item.get("track"),
            ids: [],
          };
        }
        newTracks[itemUpdate.container][item.get("track")].ids.push(
          itemUpdate.id
        );
      }

      newProjectDetails = newProjectDetails.setIn(
        [itemUpdate.container, itemUpdate.id],
        item
      );
    }
  });



  if (newTracks.workspaceItems || newTracks.audios) {
    const result = handleNewTracks({
      newTracks,
      project: newProjectDetails,
      updaterObject,
    });
    newProjectDetails = result.project;
    updaterObject = result.updaterObject;
  }

  if (mayItemsOverlap || checkItemEmptyTracks) {
    let newItems = newProjectDetails.get("workspaceItems");

    if (mayItemsOverlap) {
      const overlapResult = trackChangeItemOverlap({
        items: newItems,
        movedItemIds,
        updaterObject,
        timelineInterval,
      });
      newItems = overlapResult.newItems;
      updaterObject = overlapResult.updaterObject;
    }

    // DO NOT call removeEmptyTracks() here - it would collapse track indices
    // and destroy the item-type-based track priority ordering (TEXT, PROPERTY, IMAGE, etc).
    // Track indices should only be compacted when explicitly needed (e.g., on delete operations).
    // const emptyTrackResult = removeEmptyTracks({
    //   items: newItems,
    //   container: "workspaceItems",
    //   updaterObject,
    // });
    // newItems = emptyTrackResult.items;
    // updaterObject = emptyTrackResult.updaterObject;
    newProjectDetails = newProjectDetails.set("workspaceItems", newItems);
  }

  // === AI Reframe Scene Allocation (Run after overlap resolution) ===
  const workspaceUpdates = updaterObject.get("workspaceItems");
  const allWorkspaceItems = newProjectDetails.get("workspaceItems");

  if (workspaceUpdates && allWorkspaceItems && (reframeData || appReframeData)) {
    const processedParentIds = new Set();

    for (const [parentId, parentUpdate] of workspaceUpdates.entries()) {
      if (parentUpdate.get("isDelete") || parentUpdate.get("isAdd")) continue;
      if (processedParentIds.has(parentId)) continue;

      const parentItem = allWorkspaceItems.get(parentId);
      const isReframeScene = parentItem && (parentItem.has("sceneData") || parentId.includes("reframe") || parentId.includes("scene"));
      
      if (!parentItem || !isVideoOnly(parentItem.get("type"), parentItem.get("subType")) || isReframeScene) {
  
        continue;
      }

      if (!parentItem.get("isReframe")) {
        continue;
      }

      const assetId = parentItem.get("assetId");
      if (!assetId) {
        continue;
      }

      const reframeDataJS = reframeData?.toJS ? reframeData.toJS() : reframeData;
      const appReframeDataJS = appReframeData?.toJS ? appReframeData.toJS() : appReframeData;
      
      let assetReframeData = reframeDataJS?.[assetId] ||
        (reframeDataJS?.scenes && (reframeDataJS.assetId === assetId || !reframeDataJS.assetId) ? reframeDataJS : null);

      if (!assetReframeData && appReframeDataJS) {
        assetReframeData = appReframeDataJS?.[assetId] ||
          (appReframeDataJS?.scenes && (appReframeDataJS.assetId === assetId || !appReframeDataJS.assetId) ? appReframeDataJS : null);
      }

      if (!assetReframeData || !assetReframeData.scenes || assetReframeData.scenes.length === 0) {
        continue;
      }

      let currentWorkspaceItems = newProjectDetails.get("workspaceItems");

      // ✅ CLEANUP: Remove duplicate or overlayed scene allocations
      const parentScenes = [];
      currentWorkspaceItems.forEach((child, childId) => {
        if (child.get("dropId") === parentId && child.get("sceneData")) {
          parentScenes.push({ childId, child });
        }
      });

      const scenesByPersonKey = {};
      parentScenes.forEach(({ childId, child }) => {
        let sData = child.get("sceneData");
        if (sData && typeof sData.get !== "function") {
          sData = fromJS(sData);
        }
        const personKey = child.get("personKey") || (sData ? sData.get("personKey") : null) || "default";
        if (!scenesByPersonKey[personKey]) {
          scenesByPersonKey[personKey] = [];
        }
        scenesByPersonKey[personKey].push({ childId, child });
      });

      const deletedChildIds = new Set();
      for (const clips of Object.values(scenesByPersonKey)) {
        if (clips.length <= 1) continue;

        // Sort by enterStart to identify chronological overlaps
        clips.sort((a, b) => (a.child.get("enterStart") || 0) - (b.child.get("enterStart") || 0));

        for (let i = 0; i < clips.length; i++) {
          const clipA = clips[i];
          if (deletedChildIds.has(clipA.childId)) continue;

          const startA = clipA.child.get("enterStart") || 0;
          const endA = clipA.child.get("exitEnd") || 0;

          for (let j = i + 1; j < clips.length; j++) {
            const clipB = clips[j];
            if (deletedChildIds.has(clipB.childId)) continue;

            const startB = clipB.child.get("enterStart") || 0;
            const endB = clipB.child.get("exitEnd") || 0;

            // Check if clips overlap/overlay in time
            const overlaps = !(endA <= startB || startA >= endB);
            if (overlaps) {
              deletedChildIds.add(clipB.childId);
            }
          }
        }
      }

      if (deletedChildIds.size > 0) {
        for (const childId of deletedChildIds) {
          newProjectDetails = newProjectDetails.deleteIn(["workspaceItems", childId]);
          updaterObject = updaterObject.setIn(["workspaceItems", childId, "isDelete"], true);
        }
        // Refresh currentWorkspaceItems after deletion
        currentWorkspaceItems = newProjectDetails.get("workspaceItems");
      }

      const getFamilyId = (it) => {
        if (!it) return null;
        if (it.has("sceneData") || it.get("id")?.includes("scene")) {
          const pId = it.get("dropId");
          const p = currentWorkspaceItems.get(pId);
          return p ? (p.get("originalId") || pId) : (it.get("originalId") || it.get("dropId"));
        }
        return it.get("originalId") || it.get("id") || it.get("dropId");
      };

      const parentFamilyId = getFamilyId(parentItem);

      const currentParentSceneIds = new Map();
      const existingSceneTemplates = new Map();
      const globalFamilyScenes = new Map(); // Track all scenes from this family globally

      // Build maps of existing scenes - use latest state for accurate overlap detection
      const latestWorkspaceItems = newProjectDetails.get("workspaceItems");

      currentWorkspaceItems.forEach((child, childId) => {
        let sData = child.get("sceneData");
        if (!sData) return;
        if (sData && typeof sData.get !== "function") {
          sData = fromJS(sData);
        }
        const sId = sData.get("scene_id");
        if (sId === undefined) return;

        const pKey = child.get("personKey") || sData.get("personKey") || "";
        const compositeKey = `${sId}_${pKey}`;

        const childFamilyId = getFamilyId(child);
        if (childFamilyId === parentFamilyId) {
          if (!existingSceneTemplates.has(compositeKey)) {
            existingSceneTemplates.set(compositeKey, child);
          }
          if (child.get("dropId") === parentId) {
            currentParentSceneIds.set(compositeKey, childId);
          }
          // Track ALL scenes from this family with their LATEST positions for accurate overlap detection
          const latestChild = latestWorkspaceItems.get(childId) || child;
          if (!globalFamilyScenes.has(compositeKey)) {
            globalFamilyScenes.set(compositeKey, []);
          }
          globalFamilyScenes.get(compositeKey).push({ childId, child: latestChild });
        }
      });

      processedParentIds.add(parentId);

      const parentEnterStart = parentItem.get("enterStart") || 0;
      const parentExitEnd = parentItem.get("exitEnd") || 0;
      const parentVideoStart = parentItem.get("videoStart") !== undefined ? parentItem.get("videoStart") : 0;
      const parentSpeed = Number.isFinite(parentItem.get("speed")) ? parentItem.get("speed") : 1;
      const projectWidth = newProjectDetails.get("width") || 450;
      const projectHeight = newProjectDetails.get("height") || 800;

      // ✅ CLEANUP: Delete scenes attached to this parent based on VIDEO time validity
      // We need to check against video times, not timeline positions, because timeline positions
      // change during drag but video times remain the same
      const validSceneIds = new Set();
      for (const scene of assetReframeData.scenes) {
        const sceneId = scene.scene_id;
        const sceneStartTime = scene.start_time !== undefined ? scene.start_time : scene.start;
        const sceneEndTime = scene.end_time !== undefined ? scene.end_time : scene.end;

        if (sceneStartTime === undefined || sceneEndTime === undefined) continue;

        // Check if this scene's video time overlaps with parent's video time range
        const parentVideoEnd = parentItem.get("videoEnd") !== undefined
          ? parentItem.get("videoEnd")
          : parentItem.get("videoDuration") || Infinity;

        const isValid = !(sceneEndTime <= parentVideoStart || sceneStartTime >= parentVideoEnd);
        
        // Scene is valid if its video time overlaps with parent's video time
        if (isValid) {
          const coordinates = scene.coordinates || {};
          for (const personKey of Object.keys(coordinates)) {
            validSceneIds.add(`${sceneId}_${personKey}`);
          }
        } 
      }

      // Delete scenes that are no longer valid based on video time
      for (const [sceneChildId, sceneChildData] of currentParentSceneIds.entries()) {
        if (!validSceneIds.has(sceneChildId)) {
          const sceneChild = currentWorkspaceItems.get(sceneChildData);
          if (sceneChild) {
             const cleanupLayout = sceneChild.get("layout") || sceneChild.getIn(["sceneData", "layout"]);
            if (cleanupLayout === "FIT") {
              continue;
            }
            newProjectDetails = newProjectDetails.deleteIn(["workspaceItems", sceneChildData]);
            updaterObject = updaterObject.setIn(["workspaceItems", sceneChildData, "isDelete"], true);

            // Remove from globalFamilyScenes tracking
            const scenes = globalFamilyScenes.get(sceneChildId) || [];
            const filteredScenes = scenes.filter(s => s.childId !== sceneChildData);
            if (filteredScenes.length > 0) {
              globalFamilyScenes.set(sceneChildId, filteredScenes);
            } else {
              globalFamilyScenes.delete(sceneChildId);
            }

            // Remove from currentParentSceneIds so it doesn't get processed later
            currentParentSceneIds.delete(sceneChildId);
          }
        }
      }

      for (const scene of assetReframeData.scenes) {
        const sceneId = scene.scene_id;
        const sceneStartTime = scene.start_time !== undefined ? scene.start_time : scene.start;
        const sceneEndTime = scene.end_time !== undefined ? scene.end_time : scene.end;

        if (sceneStartTime === undefined || sceneEndTime === undefined) {
          continue;
        }

        const idealTimelineStart = parentEnterStart + (sceneStartTime - parentVideoStart) / parentSpeed;
        const idealTimelineEnd = parentEnterStart + (sceneEndTime - parentVideoStart) / parentSpeed;

        if (idealTimelineStart >= parentExitEnd || idealTimelineEnd <= parentEnterStart) {
          continue;
        }

        const coordinates = scene.coordinates || {};
        for (const personKey of Object.keys(coordinates)) {
          const personCoordinates = coordinates[personKey];
          if (!Array.isArray(personCoordinates) || personCoordinates.length === 0) {
            continue;
          }

          const compositeKey = `${sceneId}_${personKey}`;
          let childId = currentParentSceneIds.get(compositeKey);
          let child = childId ? currentWorkspaceItems.get(childId) : null;
          let isNewChild = false;

          if (!child) {
            // Check if a scene already exists for THIS parent in the expected time range
            const existingGlobalScenes = globalFamilyScenes.get(compositeKey) || [];
            let overlappingOwnScene = null;

            for (const { childId: existingId, child: existingChild } of existingGlobalScenes) {
              const existingDropId = existingChild.get("dropId");

              // Only consider scenes that belong to THIS parent
              if (existingDropId === parentId) {
                overlappingOwnScene = { childId: existingId, child: existingChild };
                break;
              }
            }

            // If scene already exists for THIS parent, reuse it (will be repositioned below)
            if (overlappingOwnScene) {
              childId = overlappingOwnScene.childId;
              child = overlappingOwnScene.child;
              // Don't set isNewChild since we're reusing an existing scene
            } else {
              const templateChild = existingSceneTemplates.get(compositeKey);

            if (templateChild) {
              childId = `scene${compositeKey}-${randomString("reframe")}`;              child = templateChild
                .set("id", childId)
                .set("drop", childId)
                .set("dropId", parentId)
                .set("originalId", parentFamilyId);

              const updatedSceneData = {
                ...scene,
                scene_id: sceneId,
                personKey,
                movable: personCoordinates.length > 1,
                frame_width: assetReframeData.video_width || scene.frame_width || 1280,
                frame_height: assetReframeData.video_height || scene.frame_height || 720,
              };
              delete updatedSceneData.coordinates;

              child = child.set("sceneData", fromJS(updatedSceneData));
              child = child.set("personKey", personKey);
            } else {
              childId = `scene${compositeKey}-${randomString("reframe")}`;

              const sceneData = {
                ...scene,
                scene_id: sceneId,
                personKey,
                movable: personCoordinates.length > 1,
                frame_width: assetReframeData.video_width || scene.frame_width || 1280,
                frame_height: assetReframeData.video_height || scene.frame_height || 720,
              };
              delete sceneData.coordinates;

              // Calculate proper dimensions and coordinates (matching reframeHelper.js buildFrameData)
              let scaledWidth = projectWidth;
              let scaledHeight = (parentItem.get("pheight") / parentItem.get("pwidth")) * scaledWidth;

              if (scaledHeight < projectHeight) {
                scaledHeight = projectHeight;
                scaledWidth = (parentItem.get("pwidth") / parentItem.get("pheight")) * scaledHeight;
              }

              const videoWidth = scaledWidth > assetReframeData.video_width ? scaledWidth : assetReframeData.video_width;
              const videoHeight = scaledHeight > assetReframeData.video_height ? scaledHeight : assetReframeData.video_height;
              const aspectRatio = videoWidth / videoHeight;

              const oHeight = videoHeight;
              const oWidth = oHeight * aspectRatio;

              // Process first coordinate
              let firstCoord = fromJS(personCoordinates[0]);
              let reframeWidth = projectWidth;
              let reframeHeight = projectHeight;
              let cWidth = projectWidth;
              let cHeight = projectHeight;

              // Apply layout processing (SPLIT, THREE, FOUR, etc.)
              const layoutType = scene.layout;
              if (layoutType && (layoutType === "SPLIT" || layoutType === "THREE" || layoutType === "FOUR")) {
                const index = Number(personKey?.split("_")[1]) - 1;
                const box = REFRAME_LAYOUT[layoutType]?.boxes?.[index];

                if (box) {
                  const scaleX = projectWidth / 450;
                  const scaleY = projectHeight / 800;

                    const scaledBox = {
                      x: box.x * scaleX,
                      y: box.y * scaleY,
                      width: box.width * scaleX,
                      height: box.height * scaleY,
                    };

                    firstCoord = firstCoord.set("wx", scaledBox.x).set("wp", scaledBox.y);
                    cWidth = scaledBox.width;
                    cHeight = scaledBox.height;
                    reframeHeight = scaledBox.height;
                    reframeWidth = scaledBox.width;
                  }
                }

                const itemWidth = scaledWidth;
                const itemHeight = scaledHeight;
                const newX = (firstCoord.get("x") / assetReframeData.video_width) * itemWidth;
                const newY = (firstCoord.get("y") / assetReframeData.video_height) * itemHeight;
                const updateX = scaledWidth > assetReframeData.video_width ? newX : firstCoord.get("x");
                const updateY = scaledHeight > assetReframeData.video_height ? newY : firstCoord.get("y");

                const coordW = firstCoord.get("w") || 0;
                const updateW = scaledWidth > assetReframeData.video_width
                  ? (coordW / assetReframeData.video_width) * itemWidth
                  : coordW;

                // Calculate original aspect ratio (matching reframeHelper.js getOriginalAspectRatio)
                let original = null;
                if (layoutType !== "FIT") {
                  const getOriginalAspectRatio = (
                    oWidth,
                    oHeight,
                    cWidth,
                    cHeight,
                    videoWidth,
                    videoHeight,
                    reframeX,
                    reframeY,
                    reframeW,
                    layout
                  ) => {
                    const targetWidth = videoWidth;
                    const targetHeight = videoHeight;

                    const result = {
                      x: (reframeX / videoWidth) * targetWidth,
                      y: (reframeY / videoHeight) * targetHeight,
                    };

                    const currentWidth = cWidth || 450;
                    const currentHeight = cHeight || 800;

                    const scaleX = currentWidth / targetWidth;
                    const scaleY = currentHeight / targetHeight;

                    const BASE_SCALE = Math.max(scaleX, scaleY);

                    const ZOOM_MAP = {
                      FILL: 1,
                      THREE: 1.65,
                      FOUR: 1.85,
                      SPLIT: 1.35,
                    };

                    const ZOOM_FACTOR = ZOOM_MAP[layout] || 1;

                    const finalScale = BASE_SCALE * ZOOM_FACTOR;

                    const scaledWidth = targetWidth * finalScale;
                    const scaledHeight = targetHeight * finalScale;

                    const personCenterX = result.x + (reframeW || 0) / 2;
                    const personCenterY = result.y;

                    const xDiff = currentWidth / 2 - personCenterX * finalScale;
                    const yDiff = currentHeight / 2 - personCenterY * finalScale;

                    const minX = currentWidth - scaledWidth;
                    const maxX = 0;

                    const minY = currentHeight - scaledHeight;
                    const maxY = 0;

                    const finalX = Math.min(maxX, Math.max(minX, xDiff));
                    const finalY = Math.min(maxY, Math.max(minY, yDiff));

                    if (layout === "FILL") {
                      return {
                        x: finalX / currentWidth,
                        y: 0,
                        width: scaledWidth / currentWidth,
                        height: 1,
                      };
                    }

                    return {
                      x: finalX / currentWidth,
                      y: finalY / currentHeight,
                      width: scaledWidth / currentWidth,
                      height: scaledHeight / currentHeight,
                    };
                  };

                  original = getOriginalAspectRatio(
                    oWidth,
                    oHeight,
                    cWidth,
                    cHeight,
                    videoWidth,
                    videoHeight,
                    updateX,
                    updateY,
                    updateW,
                    layoutType
                  );
                }

                // Calculate opacity (matching reframeHelper.js logic)
                const sceneOpacity = (layoutType !== "SPLIT") &&
                  (layoutType !== "THREE") &&
                  (layoutType !== "FOUR") &&
                  (layoutType !== "SCREENSHARE") &&
                  (layoutType !== "GAMEPLAY") &&
                  (layoutType !== "FIT") &&
                  firstCoord.get("opacity") !== undefined
                  ? firstCoord.get("opacity")
                  : 1;

                child = fromJS({
                  id: childId,
                  drop: childId,
                  dropId: parentId,
                  originalId: parentFamilyId,
                  type: parentItem.get("type"),
                  subType: parentItem.get("subType"),
                  assetId: parentItem.get("assetId"),
                  src: parentItem.get("src"),
                  thumb: parentItem.get("thumb"),
                  x: firstCoord.get("wx") || 0,
                  y: firstCoord.get("wp") || 0,
                  width: reframeWidth,
                  height: reframeHeight,
                  videoStart: sceneStartTime,
                  videoEnd: sceneEndTime,
                  enterStart: idealTimelineStart,
                  enterEnd: idealTimelineStart,
                  exitStart: idealTimelineEnd,
                  exitEnd: idealTimelineEnd,
                  track: parentItem.get("track"),
                  sceneId: `scene${sceneId}`,
                  sceneData: fromJS(sceneData),
                  personKey,
                  opacity: sceneOpacity,
                  volume: 0,
                  pwidth: parentItem.get("pwidth"),
                  pheight: parentItem.get("pheight"),
                  videoDuration: parentItem.get("videoDuration"),
                  layout: scene.layout,
                  isReframe: false,
                  original: original ? fromJS(original) : undefined,
                  isCropped: !!original,
                });
              }

              newProjectDetails = newProjectDetails.setIn(["workspaceItems", childId], child);
              currentParentSceneIds.set(compositeKey, childId);
              isNewChild = true;
            }
          }

          const sceneStart = sceneStartTime;
          const sceneEnd = sceneEndTime;

          const recalcIdealTimelineStart = parentEnterStart + (sceneStart - parentVideoStart) / parentSpeed;
          const recalcIdealTimelineEnd = parentEnterStart + (sceneEnd - parentVideoStart) / parentSpeed;
          const clampedEnterStart = Math.max(parentEnterStart, recalcIdealTimelineStart);
          const clampedExitEnd = Math.min(parentExitEnd, recalcIdealTimelineEnd);

          // ✅ OVERLAP CHECK: Ensure this scene doesn't overlap with scenes from OTHER parents
          let hasOverlap = false;
          const existingFamilyScenes = globalFamilyScenes.get(compositeKey) || [];
          for (const { childId: existingChildId, child: existingChild } of existingFamilyScenes) {
            if (existingChildId === childId) continue; // Skip self

            const existingDropId = existingChild.get("dropId");
            // Only check overlap with scenes from OTHER parents (siblings)
            if (existingDropId === parentId) continue;

            const existingEnterStart = existingChild.get("enterStart");
            const existingExitEnd = existingChild.get("exitEnd");

            // Check if the new scene time range overlaps with existing scene from sibling
            const overlaps = !(clampedExitEnd <= existingEnterStart || clampedEnterStart >= existingExitEnd);

            if (overlaps) {
              hasOverlap = true;
              break;
            }
          }

          // Skip creating/updating this scene if it would overlap with a scene from another parent
          if (hasOverlap) {
            continue;
          }

          if (clampedEnterStart >= clampedExitEnd) {
            const boundary = recalcIdealTimelineStart < parentEnterStart ? parentEnterStart : parentExitEnd;

            newProjectDetails = newProjectDetails.setIn(["workspaceItems", childId, "enterStart"], boundary);
            newProjectDetails = newProjectDetails.setIn(["workspaceItems", childId, "exitEnd"], boundary);
            updaterObject = updaterObject.setIn(["workspaceItems", childId, "data", "enterStart"], boundary);
            updaterObject = updaterObject.setIn(["workspaceItems", childId, "data", "exitEnd"], boundary);

            const newVideoStart = sceneStart + (boundary - recalcIdealTimelineStart) * parentSpeed;
            newProjectDetails = newProjectDetails.setIn(["workspaceItems", childId, "videoStart"], newVideoStart);
            updaterObject = updaterObject.setIn(["workspaceItems", childId, "data", "videoStart"], newVideoStart);

            if (child.get("videoEnd") !== undefined) {
              newProjectDetails = newProjectDetails.setIn(["workspaceItems", childId, "videoEnd"], newVideoStart);
              updaterObject = updaterObject.setIn(["workspaceItems", childId, "data", "videoEnd"], newVideoStart);
            }
          } else {
            newProjectDetails = newProjectDetails.setIn(["workspaceItems", childId, "enterStart"], clampedEnterStart);
            newProjectDetails = newProjectDetails.setIn(["workspaceItems", childId, "exitEnd"], clampedExitEnd);
            updaterObject = updaterObject.setIn(["workspaceItems", childId, "data", "enterStart"], clampedEnterStart);
            updaterObject = updaterObject.setIn(["workspaceItems", childId, "data", "exitEnd"], clampedExitEnd);

            const newVideoStart = sceneStart + (clampedEnterStart - recalcIdealTimelineStart) * parentSpeed;
            newProjectDetails = newProjectDetails.setIn(["workspaceItems", childId, "videoStart"], newVideoStart);
            updaterObject = updaterObject.setIn(["workspaceItems", childId, "data", "videoStart"], newVideoStart);

            if (child.get("videoEnd") !== undefined) {
              const newVideoEnd = sceneEnd - (recalcIdealTimelineEnd - clampedExitEnd) * parentSpeed;
              newProjectDetails = newProjectDetails.setIn(["workspaceItems", childId, "videoEnd"], newVideoEnd);
              updaterObject = updaterObject.setIn(["workspaceItems", childId, "data", "videoEnd"], newVideoEnd);
            }
          }

          const parentTrack = parentItem.get("track");
          if (child.get("track") !== parentTrack) {
            newProjectDetails = newProjectDetails.setIn(["workspaceItems", childId, "track"], parentTrack);
            updaterObject = updaterObject.setIn(["workspaceItems", childId, "data", "track"], parentTrack);
          }

          const syncKeys = [
            "enterEffectName", "enterEffectIcon", "enterEnd",
            "exitEffectName", "exitEffectIcon", "exitStart"
          ];
          for (let k = 0; k < syncKeys.length; k++) {
            const key = syncKeys[k];
            const parentVal = parentItem.get(key);
            if (child.get(key) !== parentVal) {
              newProjectDetails = newProjectDetails.setIn(["workspaceItems", childId, key], parentVal);
              updaterObject = updaterObject.setIn(["workspaceItems", childId, "data", key], parentVal);
            }
          }

          if (isNewChild) {
            updaterObject = updaterObject.setIn(["workspaceItems", childId, "isNew"], true);
            const finalItem = newProjectDetails.getIn(["workspaceItems", childId]);
            updaterObject = updaterObject.setIn(["workspaceItems", childId, "data"], finalItem);

            // ✅ Add newly created scene to globalFamilyScenes to track for future overlap checks
            if (!globalFamilyScenes.has(compositeKey)) {
              globalFamilyScenes.set(compositeKey, []);
            }
            globalFamilyScenes.get(compositeKey).push({ childId, child: finalItem });
          } else {
            // ✅ Update existing scene in globalFamilyScenes with new time values
            const updatedItem = newProjectDetails.getIn(["workspaceItems", childId]);
            const existingScenes = globalFamilyScenes.get(compositeKey) || [];
            const sceneIndex = existingScenes.findIndex(s => s.childId === childId);
            if (sceneIndex !== -1) {
              existingScenes[sceneIndex].child = updatedItem;
            }
          }
        }
      }

      // === FIT gap-fill: cover reframe-less regions of this parent ===
      // After reframe scenes are allocated, any portion of the parent's timeline range
      // [parentEnterStart, parentExitEnd] that is NOT covered by a reframe scene must be
      // filled with a FIT scene so playback shows the raw (un-reframed) video for that gap.
      // This is intentionally robust to MULTIPLE gaps (e.g. reframe data for 0-20s and
      // 30-40s, then expanding to cover 0-35s leaves a 20-30s gap) and is idempotent, so
      // repeated expansions keep producing the correct set of FIT scenes.
      {
        const MIN_GAP = Math.max(timelineInterval || 0, 0.001); // ignore sub-frame slivers
        const fitLatestItems = newProjectDetails.get("workspaceItems");

        // 1. Collect covered timeline intervals from this parent's NON-FIT reframe scenes.
        const coveredIntervals = [];
        fitLatestItems.forEach((child) => {
          if (!child || !child.get("sceneData")) return;
          if (child.get("dropId") !== parentId) return;
          const childLayout = child.get("layout") || child.getIn(["sceneData", "layout"]);
          if (childLayout === "FIT") return; // FIT scenes are fillers, not coverage
          const cs = child.get("enterStart");
          const ce = child.get("exitEnd");
          if (Number.isFinite(cs) && Number.isFinite(ce) && ce > cs) {
            const clampedStart = Math.max(cs, parentEnterStart);
            const clampedEnd = Math.min(ce, parentExitEnd);
            if (clampedEnd > clampedStart) {
              coveredIntervals.push([clampedStart, clampedEnd]);
            }
          }
        });

        // 2. Merge overlapping/adjacent covered intervals.
        coveredIntervals.sort((a, b) => a[0] - b[0]);
        const mergedCovered = [];
        for (const [cs, ce] of coveredIntervals) {
          const last = mergedCovered[mergedCovered.length - 1];
          if (last && cs <= last[1] + 1e-6) {
            last[1] = Math.max(last[1], ce);
          } else {
            mergedCovered.push([cs, ce]);
          }
        }

        // 3. Derive the gaps = parent range minus covered intervals.
        const gaps = [];
        let gapCursor = parentEnterStart;
        for (const [cs, ce] of mergedCovered) {
          if (cs - gapCursor > MIN_GAP) {
            gaps.push([gapCursor, cs]);
          }
          gapCursor = Math.max(gapCursor, ce);
        }
        if (parentExitEnd - gapCursor > MIN_GAP) {
          gaps.push([gapCursor, parentExitEnd]);
        }

        // 4. Reconcile existing FIT scenes against the computed gaps:
        //    keep the ones that still match a gap exactly, delete the stale ones.
        const gapSatisfied = new Array(gaps.length).fill(false);
        for (const [childId, child] of fitLatestItems.entrySeq()) {
          if (!child || !child.get("sceneData")) continue;
          if (child.get("dropId") !== parentId) continue;
          const childLayout = child.get("layout") || child.getIn(["sceneData", "layout"]);
          if (childLayout !== "FIT") continue;
          const cs = child.get("enterStart");
          const ce = child.get("exitEnd");
          let matchIdx = -1;
          for (let gj = 0; gj < gaps.length; gj += 1) {
            if (Math.abs(gaps[gj][0] - cs) < 1e-3 && Math.abs(gaps[gj][1] - ce) < 1e-3) {
              matchIdx = gj;
              break;
            }
          }
          // Delete a FIT scene when it does not exactly match a current gap (stale, now
          // covered by a reframe scene, or outside the clip) OR when its gap is ALREADY
          // satisfied by another FIT scene (duplicate). This guarantees at most one FIT
          // scene per gap, so FIT scenes can never overlap each other at any expansion count.
          if (matchIdx === -1 || gapSatisfied[matchIdx]) {
            newProjectDetails = newProjectDetails.deleteIn(["workspaceItems", childId]);
            updaterObject = updaterObject.setIn(["workspaceItems", childId, "isDelete"], true);
          } else {
            gapSatisfied[matchIdx] = true;
          }
        }

        // 5. Create a FIT scene for every gap not already satisfied by an existing FIT scene.
        const fitScaleX = projectWidth / 450;
        const fitScaleY = projectHeight / 800;
        const parentFamily = parentItem.get("originalId") || parentId;
        for (let gi = 0; gi < gaps.length; gi += 1) {
          if (gapSatisfied[gi]) continue;
          const [gapStart, gapEnd] = gaps[gi];
          const fitSceneId = randomString("scene");
          const fitVideoStart = parentVideoStart + (gapStart - parentEnterStart) * parentSpeed;
          const fitVideoEnd = parentVideoStart + (gapEnd - parentEnterStart) * parentSpeed;

          const fitScene = fromJS({
            id: fitSceneId,
            drop: fitSceneId,
            dropId: parentId,
            originalId: parentFamily,
            type: parentItem.get("type"),
            subType: parentItem.get("subType"),
            assetId: parentItem.get("assetId"),
            src: parentItem.get("src"),
            thumb: parentItem.get("thumb"),
            x: 0,
            y: 267.50144927536223 * fitScaleY,
            width: 450 * fitScaleX,
            height: 252.92740046838406 * fitScaleY,
            videoStart: fitVideoStart,
            videoEnd: fitVideoEnd,
            enterStart: gapStart,
            exitEnd: gapEnd,
            enterEnd: gapStart,
            exitStart: gapEnd,
            track: parentItem.get("track"),
            sceneId: `scene${fitSceneId.replace("scene", "")}`,
            layout: "FIT",
            isReframe: false,
            isCropped: false,
            volume: 0,
            opacity: 1,
            speed: parentSpeed,
            pwidth: parentItem.get("pwidth"),
            pheight: parentItem.get("pheight"),
            videoDuration: parentItem.get("videoDuration"),
            sceneData: fromJS({
              scene_id: fitSceneId.replace("scene", ""),
              layout: "FIT",
              start_time: fitVideoStart,
              end_time: fitVideoEnd,
              frame_width: parentItem.get("pwidth") || 1280,
              frame_height: parentItem.get("pheight") || 720,
            }),
          });

          newProjectDetails = newProjectDetails.setIn(["workspaceItems", fitSceneId], fitScene);
          updaterObject = updaterObject
            .setIn(["workspaceItems", fitSceneId, "isNew"], true)
            .setIn(["workspaceItems", fitSceneId, "data"], fitScene);
        }
      }
      // === End FIT gap-fill ===
    }
  }
  // === End AI Reframe Scene Allocation ===

  // Normalize transitions on reframe scene children:
  // Only boundary scenes should carry transition metadata.
  // - transitionExit* lives on FIRST scene(s) of the right clip (min enterStart)
  // - transitionEnter* lives on LAST scene(s) of the left clip (max enterStart)
  // This prevents middle scenes from accidentally inheriting transitions (e.g. via replace/template reuse).
  try {
    const wsItemsNow = newProjectDetails.get("workspaceItems");
    if (wsItemsNow) {
      const scenesByParent = {};
      wsItemsNow.forEach((it, itId) => {
        if (!it || !it.get("sceneData")) return;
        const parentId = it.get("dropId");
        if (!parentId) return;
        if (!scenesByParent[parentId]) scenesByParent[parentId] = [];
        scenesByParent[parentId].push({ id: itId, item: it });
      });

      const setWsItemField = (id, key, value) => {
        const cur = newProjectDetails.getIn(["workspaceItems", id]);
        if (!cur) return;
        if (cur.get(key) === value) return;
        newProjectDetails = newProjectDetails.setIn(["workspaceItems", id, key], value);
        updaterObject = updaterObject.setIn(["workspaceItems", id, "data", key], value);
      };

      const clearTransitionExit = (sceneId, neighborId) => {
        setWsItemField(sceneId, "transitionExitId", "none");
        setWsItemField(sceneId, "transitionExitEffect", "none_transition");
        setWsItemField(sceneId, "isTransition", false);

        const cur = newProjectDetails.getIn(["workspaceItems", sceneId]);
        if (cur) {
          const enterBackup = cur.get("enterEffectNameBackup");
          if (enterBackup && enterBackup !== "no_Effect") {
            setWsItemField(sceneId, "enterEffectName", enterBackup);
            setWsItemField(sceneId, "enterStart", cur.get("enterStartBackup"));
            setWsItemField(sceneId, "enterEnd", cur.get("enterEndBackup"));
            setWsItemField(sceneId, "enterEffectIcon", cur.get("enterEffectIconBackup"));
          }
          setWsItemField(sceneId, "enterEffectNameBackup", "no_Effect");
          setWsItemField(sceneId, "enterStartBackup", 0);
          setWsItemField(sceneId, "enterEndBackup", 0);
          setWsItemField(sceneId, "enterEffectIconBackup", "");
        }

        if (neighborId && neighborId !== "none") {
          const neighbor = newProjectDetails.getIn(["workspaceItems", neighborId]);
          if (neighbor && neighbor.get("transitionEnterId") === sceneId) {
            setWsItemField(neighborId, "transitionEnterId", "none");
            setWsItemField(neighborId, "transitionEnterEffect", "none_transition");
            setWsItemField(neighborId, "isTransition", false);

            const exitBackup = neighbor.get("exitEffectNameBackup");
            if (exitBackup && exitBackup !== "no_Effect") {
              setWsItemField(neighborId, "exitEffectName", exitBackup);
              setWsItemField(neighborId, "exitStart", neighbor.get("exitStartBackup"));
              setWsItemField(neighborId, "exitEnd", neighbor.get("exitEndBackup"));
              setWsItemField(neighborId, "exitEffectIcon", neighbor.get("exitEffectIconBackup"));
            }
            setWsItemField(neighborId, "exitEffectNameBackup", "no_Effect");
            setWsItemField(neighborId, "exitStartBackup", 0);
            setWsItemField(neighborId, "exitEndBackup", 0);
            setWsItemField(neighborId, "exitEffectIconBackup", "");
          }
        }
      };

      const clearTransitionEnter = (sceneId, neighborId) => {
        setWsItemField(sceneId, "transitionEnterId", "none");
        setWsItemField(sceneId, "transitionEnterEffect", "none_transition");
        setWsItemField(sceneId, "isTransition", false);

        const cur = newProjectDetails.getIn(["workspaceItems", sceneId]);
        if (cur) {
          const exitBackup = cur.get("exitEffectNameBackup");
          if (exitBackup && exitBackup !== "no_Effect") {
            setWsItemField(sceneId, "exitEffectName", exitBackup);
            setWsItemField(sceneId, "exitStart", cur.get("exitStartBackup"));
            setWsItemField(sceneId, "exitEnd", cur.get("exitEndBackup"));
            setWsItemField(sceneId, "exitEffectIcon", cur.get("exitEffectIconBackup"));
          }
          setWsItemField(sceneId, "exitEffectNameBackup", "no_Effect");
          setWsItemField(sceneId, "exitStartBackup", 0);
          setWsItemField(sceneId, "exitEndBackup", 0);
          setWsItemField(sceneId, "exitEffectIconBackup", "");
        }

        if (neighborId && neighborId !== "none") {
          const neighbor = newProjectDetails.getIn(["workspaceItems", neighborId]);
          if (neighbor && neighbor.get("transitionExitId") === sceneId) {
            setWsItemField(neighborId, "transitionExitId", "none");
            setWsItemField(neighborId, "transitionExitEffect", "none_transition");
            setWsItemField(neighborId, "isTransition", false);

            const enterBackup = neighbor.get("enterEffectNameBackup");
            if (enterBackup && enterBackup !== "no_Effect") {
              setWsItemField(neighborId, "enterEffectName", enterBackup);
              setWsItemField(neighborId, "enterStart", neighbor.get("enterStartBackup"));
              setWsItemField(neighborId, "enterEnd", neighbor.get("enterEndBackup"));
              setWsItemField(neighborId, "enterEffectIcon", neighbor.get("enterEffectIconBackup"));
            }
            setWsItemField(neighborId, "enterEffectNameBackup", "no_Effect");
            setWsItemField(neighborId, "enterStartBackup", 0);
            setWsItemField(neighborId, "enterEndBackup", 0);
            setWsItemField(neighborId, "enterEffectIconBackup", "");
          }
        }
      };

      Object.keys(scenesByParent).forEach((parentId) => {
        const group = scenesByParent[parentId];
        if (!group || group.length === 0) return;

        let minEnterStart = Infinity;
        let maxEnterStart = -Infinity;
        group.forEach(({ item }) => {
          const es = Number.isFinite(item.get("enterStart")) ? item.get("enterStart") : 0;
          if (es < minEnterStart) minEnterStart = es;
          if (es > maxEnterStart) maxEnterStart = es;
        });

        group.forEach(({ id, item }) => {
          const es = Number.isFinite(item.get("enterStart")) ? item.get("enterStart") : 0;

          const exitNeighborId = item.get("transitionExitId");
          const exitEffect = item.get("transitionExitEffect");
          const hasExit = (exitNeighborId && exitNeighborId !== "none") || (exitEffect && exitEffect !== "none_transition");
          if (hasExit && es !== minEnterStart) {
            clearTransitionExit(id, exitNeighborId);
          }

          const enterNeighborId = item.get("transitionEnterId");
          const enterEffect = item.get("transitionEnterEffect");
          const hasEnter = (enterNeighborId && enterNeighborId !== "none") || (enterEffect && enterEffect !== "none_transition");
          if (hasEnter && es !== maxEnterStart) {
            clearTransitionEnter(id, enterNeighborId);
          }
        });
      });
    }
  } catch (e) {
    // Never block autosave flow due to normalization
  }

  if (mayAudiosOverlap || checkAudioEmptyTracks) {
    let newAudios = newProjectDetails.get("audios");

    if (mayAudiosOverlap) {
      const overlapResult = trackChangeMediaOverlap({
        container: "audios",
        mediaData: newAudios,
        movedMediaIds: movedAudioIds,
        updaterObject,
        timelineInterval,
      });
      newAudios = overlapResult.newMedia;
      updaterObject = overlapResult.updaterObject;
    }

    // DO NOT call removeEmptyTracks() here for audios - preserve track indices
    // const emptyTrackResult = removeEmptyTracks({
    //   items: newAudios,
    //   container: "audios",
    //   updaterObject,
    // });
    // newAudios = emptyTrackResult.items;
    // updaterObject = emptyTrackResult.updaterObject;
    newProjectDetails = newProjectDetails.set("audios", newAudios);
  }

  if (mayVideosOverlap) {
    const overlapResult = trackChangeMediaOverlap({
      container: "workspaceBG",
      mediaData: newProjectDetails.get("workspaceBG"),
      movedMediaIds: movedVideoIds,
      updaterObject,
      timelineInterval,
    });
    const newVideos = overlapResult.newMedia;
    updaterObject = overlapResult.updaterObject;
    newProjectDetails = newProjectDetails.set("workspaceBG", newVideos);
  }

  // === Subtitle Reconciliation on Clip Expansion/Shrink/Trim ===
  const allLiveDropIds = new Set();
  const containersToProcess = ["workspaceItems", "workspaceBG"];
  containersToProcess.forEach(cn => {
    newProjectDetails.get(cn)?.forEach((wsItem) => {
      const d = wsItem.get("dropId");
      if (d) allLiveDropIds.add(d);
    });
  });

  const globalProcessedFamilies = new Set();
  containersToProcess.forEach((containerName) => {
    const currentContainerItems = newProjectDetails.get(containerName);
    const origContainerItems = projectDetails.get(containerName) || fromJS({});

    if (currentContainerItems && newProjectDetails.getIn(["subtitle", "data"])) {
      currentContainerItems.forEach((item, itemId) => {
        const isVideo = isVideoOnly(item.get("type"), item.get("subType"));
        if (item && isVideo && !item.get("sceneData")) {
          const isReframeClip = item.get("isReframe") === true;
          const familyId = item.get("reframeOriginalId") || item.get("originalId") || itemId;
          const assetId = item.get("assetId");
          const src = item.getIn(["assetData", "src"]) || item.get("src");
          const familyKey = isReframeClip ? familyId : (assetId || src || familyId);

          if (globalProcessedFamilies.has(familyKey)) return;

          const origItem = origContainerItems.get(itemId);
          const videoStart = item.get("videoStart") || 0;
          const videoEnd = item.get("videoEnd") || 0;

          const isBoundaryChanged =
            !origItem ||
            origItem.get("videoStart") !== videoStart ||
            origItem.get("videoEnd") !== videoEnd ||
            origItem.get("enterStart") !== item.get("enterStart") ||
            origItem.get("playStart") !== item.get("playStart") ||
            origItem.get("exitEnd") !== item.get("exitEnd") ||
            origItem.get("playEnd") !== item.get("playEnd");

          if (isBoundaryChanged) {
            const dropId = item.get("dropId");

            globalProcessedFamilies.add(familyKey);

            // Find all sibling parent clips from the same family
            const siblings = currentContainerItems.filter(
              (sibling, siblingId) =>
                siblingId !== itemId &&
                isVideoOnly(sibling.get("type"), sibling.get("subType")) &&
                !sibling.get("sceneData") &&
                (
                  (item.get("dropId") && sibling.get("dropId") === item.get("dropId")) ||
                  (item.get("originalId") && sibling.get("originalId") === item.get("originalId")) ||
                  (sibling.get("originalId") === familyId || siblingId === familyId) ||
                  (item.get("reframeOriginalId") && sibling.get("reframeOriginalId") === item.get("reframeOriginalId")) ||
                  (sibling.get("reframeOriginalId") === familyId || siblingId === familyId) ||
                  (!isReframeClip && assetId && sibling.get("assetId") === assetId) ||
                  (!isReframeClip && src && (sibling.getIn(["assetData", "src"]) === src || sibling.get("src") === src))
                )
            );

            // Build family clips list including the active item
            const familyClips = [];
            familyClips.push({
              itemId,
              dropId,
              videoStart,
              videoEnd
            });

            siblings.forEach((sibling, siblingId) => {
              familyClips.push({
                itemId: siblingId,
                dropId: sibling.get("dropId"),
                videoStart: sibling.get("videoStart") || 0,
                videoEnd: sibling.get("videoEnd") || 0
              });
            });

            // Collect the set of all dropIds belonging to live family clips
            const familyDropIds = new Set(familyClips.map(c => c.dropId));


            // After building familyClips, find orphaned dropIds from deleted siblings.
            const orphanedDropIds = new Set();
            if (origContainerItems) {
              origContainerItems.forEach((origWsItem, origWsId) => {
                if (!origWsItem || origWsItem.get("sceneData")) return;
                if (!isVideoOnly(origWsItem.get("type"), origWsItem.get("subType"))) return;
                // Check if this item was part of the family
                const origFamilyId = origWsItem.get("originalId") || origWsId;
                const origAssetId = origWsItem.get("assetId");
                const origSrc = origWsItem.getIn(["assetData", "src"]) || origWsItem.get("src");
                const isSameFamily =
                  (item.get("dropId") && origWsItem.get("dropId") === item.get("dropId")) ||
                  (item.get("originalId") && origFamilyId === item.get("originalId")) ||
                  (origFamilyId === familyId || origWsId === familyId) ||
                  (!isReframeClip && assetId && origAssetId === assetId) ||
                  (!isReframeClip && src && (origSrc === src));
                if (!isSameFamily) return;
                // Check if this item has been deleted
                const origDropId = origWsItem.get("dropId");
                if (!currentContainerItems.has(origWsId) && !familyDropIds.has(origDropId)) {
                  const orphanSubMap = newProjectDetails.getIn(["subtitle", "data", origDropId]);
                  if (orphanSubMap && orphanSubMap.size > 0) {
                    orphanedDropIds.add(origDropId);
                  }
                }
              });
            }

            const subtitleDataMap = newProjectDetails.getIn(["subtitle", "data"]);
            if (subtitleDataMap) {
              subtitleDataMap.forEach((subMap, candidateDropId) => {
                if (allLiveDropIds.has(candidateDropId)) return; // owned by a live clip
                if (familyDropIds.has(candidateDropId)) return;  // already in family
                if (orphanedDropIds.has(candidateDropId)) return; // already found

                if (subMap && subMap.size > 0) {
                  // Check if this orphaned dropId's subtitles overlap with the expanding clip
                  // We can't check timeline position (clip is gone), so check video time overlap
                  // against the expanding clip's new video range
                  let hasOverlap = false;
                  subMap.forEach((subItem) => {
                    const subStart = subItem.get("start");
                    const subEnd = subItem.get("end");
                    const clipVideoStart = item.get("videoStart") || 0;
                    const clipVideoEnd = item.get("videoEnd") || 0;
                    if (!(clipVideoEnd <= subStart || clipVideoStart >= subEnd)) {
                      hasOverlap = true;
                    }
                  });

                  if (hasOverlap) {
                    orphanedDropIds.add(candidateDropId);
                  }
                }
              });
            }


            // Collect all subtitles from family clips AND orphaned dropIds
            const familySubtitles = [];
            const seenSubIds = new Set();
            const seenSubSignatures = new Set();

            const allDropIdsToScan = new Set();
            familyClips.forEach(c => allDropIdsToScan.add(c.dropId));
            orphanedDropIds.forEach(id => allDropIdsToScan.add(id));

            for (const scanDropId of allDropIdsToScan) {
              let subMap = newProjectDetails.getIn(["subtitle", "data", scanDropId]);

              const hadDelete = subtitleItemDeleteDropIds.has(scanDropId);
              if ((!subMap || subMap.size === 0) && !hadDelete) {
                subMap = projectDetails.getIn(["subtitle", "data", scanDropId]);
              }

              if (subMap) {
                const duplicateSubs = [];

                subMap.forEach((subItem, subId) => {
                  if (!seenSubIds.has(subId)) {
                    seenSubIds.add(subId);

                    const signature = `${subItem.get("start")}-${subItem.get("end")}`;
                    if (seenSubSignatures.has(signature)) {
                      // Collect duplicates instead of mutating inside the loop
                      const familyClip = familyClips.find(c => c.dropId === scanDropId);
                      const ownerParentId = familyClip ? familyClip.itemId : scanDropId;
                      duplicateSubs.push({ subId, ownerParentId });
                      return;
                    }
                    seenSubSignatures.add(signature);

                    const familyClip = familyClips.find(c => c.dropId === scanDropId);
                    familySubtitles.push({
                      subId,
                      subItem,
                      currentDropId: scanDropId,
                      currentParentId: familyClip ? familyClip.itemId : null,
                    });
                  }
                });

                // Process duplicates outside the loop function — no unsafe closure references
                for (const { subId, ownerParentId } of duplicateSubs) {
                  updaterObject = updaterObject.setIn(
                    ["subtitle", "subtitleData", scanDropId, subId],
                    fromJS({
                      timelineId: `${ownerParentId}-${scanDropId}-${subId}`,
                      isDelete: true,
                    })
                  );
                  newProjectDetails = newProjectDetails.deleteIn([
                    "subtitle",
                    "data",
                    scanDropId,
                    subId,
                  ]);
                }
              }
            }

            // Distribute each subtitle to ALL clips whose video range covers it.
            // When clips overlap in video time (after expansion), the subtitle must exist
            // under both dropIds so buildLocalSubtitles can display it for each clip.
            familySubtitles.forEach(({ subId, subItem }) => {
              const subStart = subItem.get("start");
              const subEnd = subItem.get("end");

              const coveringClips = [];
              const nonCoveringClips = [];

              familyClips.forEach((clip) => {
                // Video time coverage
                const coversVideo = !(clip.videoEnd <= subStart || clip.videoStart >= subEnd);

                // Timeline position valid check
                const clipEnterStart = currentContainerItems.getIn([clip.itemId, "enterStart"]) || 0;
                const clipExitEnd = currentContainerItems.getIn([clip.itemId, "exitEnd"]) || 0;
                const hasValidTimelineRange = clipExitEnd > clipEnterStart;

                if (coversVideo && hasValidTimelineRange) {
                  coveringClips.push(clip);
                } else {
                  nonCoveringClips.push(clip);
                }
              });


              // Add subtitle to ALL covering clips' dropIds
              coveringClips.forEach((clip) => {
                const hasSub = newProjectDetails.hasIn(["subtitle", "data", clip.dropId, subId]);
                if (!hasSub) {

                  newProjectDetails = newProjectDetails.setIn(
                    ["subtitle", "data", clip.dropId, subId],
                    subItem
                  );

                  updaterObject = updaterObject.setIn(
                    ["subtitle", "subtitleData", clip.dropId, subId],
                    fromJS({
                      timelineId: `${clip.itemId}-${clip.dropId}-${subId}`,
                      isNew: true,
                      data: subItem,
                    })
                  );
                }
              });

              // Remove subtitle from non-covering clips and orphaned dropIds
              const coveringDropIds = new Set(coveringClips.map(c => c.dropId));
              const allNonCoveringDropIds = new Set([
                ...nonCoveringClips.map(c => c.dropId),
                ...orphanedDropIds,
              ]);

              for (const checkDropId of allNonCoveringDropIds) {
                if (coveringDropIds.has(checkDropId)) continue;
                if (allLiveDropIds.has(checkDropId)) continue;

                if (newProjectDetails.hasIn(["subtitle", "data", checkDropId, subId])) {
                  const ownerClip = familyClips.find(c => c.dropId === checkDropId);
                  const ownerParentId = ownerClip ? ownerClip.itemId : checkDropId;
                  newProjectDetails = newProjectDetails.deleteIn([
                    "subtitle",
                    "data",
                    checkDropId,
                    subId,
                  ]);

                  updaterObject = updaterObject.setIn(
                    ["subtitle", "subtitleData", checkDropId, subId],
                    fromJS({
                      timelineId: `${ownerParentId}-${checkDropId}-${subId}`,
                      isDelete: true,
                    })
                  );

                  subtitleItemDeleteDropIds = subtitleItemDeleteDropIds.add(checkDropId);
                }
              }
            });
          }
        }
      });
    }
  });
  // === End Subtitle Reconciliation ===

  const newSubtitle = newProjectDetails.get("subtitle");
  if (willEmit && newSubtitle) {
    for (const [container, removedIds] of removedMediaDropIds.entrySeq()) {
      for (const dropId of removedIds.keySeq()) {
        const dropHasSubtitle = newProjectDetails.hasIn(["subtitle", "data", dropId]);
        if (dropHasSubtitle) {
          const hasDropId = newProjectDetails.get(container).some(item => item.get("dropId") === dropId);
          // ✅ Before deleting, check if subtitle was already migrated
          const subtitleAlreadyMigrated = updaterObject
            .getIn(["subtitle", "subtitleData", dropId]) !== undefined;

          if (!hasDropId && !subtitleAlreadyMigrated) {
            updaterObject = updaterObject.setIn(
              ["subtitle", "subtitleDropData", dropId, "isDelete"],
              true
            );
          }
        }
      }
    }
  }

  if (subtitleItemDeleteDropIds.size && updaterObject.get("subtitle")) {
    for (const dropId of subtitleItemDeleteDropIds.valueSeq()) {
      const dropData = newProjectDetails.getIn(["subtitle", "data", dropId]);
      if (dropData && dropData.size === 0) {
        // all subtitle items of this dropId is deleted, so delete whole dropId
        newProjectDetails = newProjectDetails.deleteIn(["subtitle", "data", dropId]);
        const subtitleUpdater = fromJS({ isDelete: true });
        updaterObject = updaterObject.setIn(["subtitle", "subtitleDropData", dropId], subtitleUpdater);
        // remove whole map of individual delete updater
        updaterObject = updaterObject.deleteIn(["subtitle", "subtitleData", dropId]);
      }
    }
    const subtitleData = updaterObject.getIn(["subtitle", "subtitleData"]);
    if (subtitleData && subtitleData.size === 0) {
      updaterObject = updaterObject.deleteIn(["subtitle", "subtitleData"]);
    }
  }

  // Detect any new dropId in subtitle data and register it in subtitleDropData as isNew
  const newSubData = newProjectDetails.getIn(["subtitle", "data"]);
  const oldSubData = projectDetails.getIn(["subtitle", "data"]);
  if (newSubData) {
    for (const [dropId, dropData] of newSubData.entrySeq()) {
      const isOldDrop = oldSubData && oldSubData.has(dropId);
      const existingDropUpdater = updaterObject.getIn(["subtitle", "subtitleDropData", dropId]);
      const existingIsDelete = existingDropUpdater && existingDropUpdater.get("isDelete") === true;
      // If the backend container got marked for deletion but the dropId is genuinely new,
      // we must register it as `isNew` so the backend re-creates the container before applying items.
      const shouldRegisterAsNew = !isOldDrop && (!existingDropUpdater || existingIsDelete);
      if (shouldRegisterAsNew) {
        updaterObject = updaterObject.setIn(
          ["subtitle", "subtitleDropData", dropId],
          fromJS({
            isNew: true,
            data: dropData,
          })
        );
      }
    }
  }

  if (updaterObject.get("subtitle")) {
    updaterObject = updaterObject.setIn(["subtitle", "id"], newProjectDetails.get("defaultSubtitle"));
  }

  if (newProjectDetails !== projectDetails) {
    newProjectDetails = newProjectDetails.set(
      "duration",
      getProjectDuration({ project: newProjectDetails })
    );
    if (newProjectDetails.get("duration") !== projectDetails.get("duration")) {
      updaterObject = updaterObject.setIn(
        ["project", "data", "duration"],
        newProjectDetails.get("duration")
      );
    }
  }

  if (subtitleList) {
    updaterObject = updaterObject.setIn(
      ["project", "data", "subtitle_data"],
      subtitleList
    );
  }

  if (updaterObject && updaterObject.get("workspaceItems")) {
    updaterObject = updaterObject.updateIn(["workspaceItems"], workspaceItems =>
      workspaceItems.map(item =>
        item.get("isDelete") ? item.delete("data") : item
      )
    );
  }

  // Safely access workspaceItems
  const workspaceItems = (updaterObject.toJS() || {}).workspaceItems || {};

  // Get keys with isDelete === true
  const keysWithIsDeleteTrue = Object.keys(workspaceItems).filter((key) => {
    if (workspaceItems[key]?.isDelete) {
      return workspaceItems[key]?.isDelete === true
    }
    return false
  }
  );

  if (keysWithIsDeleteTrue.length > 0) {
    const deletedIdSet = new Set(keysWithIsDeleteTrue);
    newProjectDetails.get("workspaceItems").forEach((item) => {
      const currentItemId = item.get("id");
      if (!currentItemId || deletedIdSet.has(currentItemId)) return;

      const transitionExitId = item.get("transitionExitId");
      if (transitionExitId && transitionExitId !== "none" && deletedIdSet.has(transitionExitId)) {
        updaterObject = updaterObject
          .setIn(["workspaceItems", currentItemId, "data", "transitionExitId"], "none")
          .setIn(["workspaceItems", currentItemId, "data", "transitionExitEffect"], "none_transition")
          .setIn(["workspaceItems", currentItemId, "data", "isTransition"], false);
      }

      const transitionEnterId = item.get("transitionEnterId");
      if (transitionEnterId && transitionEnterId !== "none" && deletedIdSet.has(transitionEnterId)) {
        updaterObject = updaterObject
          .setIn(["workspaceItems", currentItemId, "data", "transitionEnterId"], "none")
          .setIn(["workspaceItems", currentItemId, "data", "transitionEnterEffect"], "none_transition")
          .setIn(["workspaceItems", currentItemId, "data", "isTransition"], false);
      }
    });
  }

  return updaterObject;
};

/**
 * returns autosave updater for passed subtitle data
 * @param {object} params
 * @param {Immutable.Map | object} params.subtitleDropMap
 * @param {string} params.subtitleId
 */
export const getSubtitleUpdater = (params = {}) => {
  const { subtitleDropMap, subtitleId } = params;

  let dropMap = subtitleDropMap;
  if (!isImmutable(dropMap)) {
    dropMap = fromJS(dropMap);
  }

  let updaterObject = fromJS({});
  for (const [dropId, dropData] of subtitleDropMap.entrySeq()) {
    const subtitleUpdater = fromJS({ isNew: true, data: dropData });
    updaterObject = updaterObject.setIn(
      [
        "subtitle",
        "subtitleDropData",
        dropId,
      ],
      subtitleUpdater
    );
  }

  if (updaterObject.get("subtitle")) {
    updaterObject = updaterObject.setIn(["subtitle", "id"], subtitleId);
  }

  return updaterObject;
}

/**
 * Function to apply changes of timeline emitted by user
 * @param {object} params
 * @param {object} params.updaterObject updaterObject from autosave/undo
 * @param {object} params.projectDetails project state from redux
 * @returns {object}
 */
export const applyTimelineUpdaterObject = (params = {}) => {
  const { projectDetails, updaterObject } = params;
  let newProjectDetails = projectDetails;

  let itemsMightChangeTrack = false;
  let audiosMightChangeTrack = false;
  let shouldBuildSubtitle = false;
  let subtitlesToSort = ImmutableSet();

  for (const [container, containerData] of updaterObject.entrySeq()) {
    if (container === "project") {
      for (const updateEntry of containerData.get("data").entrySeq()) {
        let key = updateEntry[0];
        const val = updateEntry[1];
        if (key === "subtitle_data") {
          key = "subtitleData";
          const activeSubtitle = val.find((entry) => entry.get("isActive"));
          if (activeSubtitle) {
            newProjectDetails = newProjectDetails.set(
              "defaultSubtitle",
              activeSubtitle.get("subtitleId")
            );
          } else {
            newProjectDetails = newProjectDetails.set("defaultSubtitle", "");
          }
          shouldBuildSubtitle = true;
        }
        newProjectDetails = newProjectDetails.set(key, val);
      }
    } else if (container === "subtitle") {
      if (containerData.get("id") !== newProjectDetails.get("defaultSubtitle")) {
        // someother subtitle is selected currently by current user
        continue;
      }

      if (containerData.has("subtitleGlobal")) {
        for (const [key, value] of containerData.get("subtitleGlobal").entrySeq()) {
          newProjectDetails = newProjectDetails.setIn(["subtitle", key], fromJS(value));
        }
      }
      if (containerData.has("subtitleDropData")) {
        for (const [dropId, subtitleDropUpdater] of containerData.get("subtitleDropData").entrySeq()) {
          if (subtitleDropUpdater.get("isNew")) {
            newProjectDetails = newProjectDetails.setIn(
              ["subtitle", "data", dropId],
              subtitleDropUpdater.get("data")
            );
            subtitlesToSort = subtitlesToSort.add(dropId);
            shouldBuildSubtitle = true;
          } else if (subtitleDropUpdater.get("isDelete")) {
            newProjectDetails = newProjectDetails.deleteIn([
              "subtitle",
              "data",
              dropId,
            ]);
            shouldBuildSubtitle = true;
          } else {
            // no need to implement this else case, pls use `subtitleData` container
          }
        }
      }
      if (containerData.has("subtitleData")) {
        for (const [dropId, subtitleItems] of containerData.get("subtitleData").entrySeq()) {
          subtitlesToSort = subtitlesToSort.add(dropId);
          for (const [
            subtitleItemId,
            subtitleUpdater,
          ] of subtitleItems.entrySeq()) {
            if (subtitleUpdater.get("isNew")) {
              newProjectDetails = newProjectDetails.setIn(
                ["subtitle", "data", dropId, subtitleItemId],
                subtitleUpdater.get("data")
              );
              subtitlesToSort = subtitlesToSort.add(dropId);
              shouldBuildSubtitle = true;
            } else if (subtitleUpdater.get("isDelete")) {
              newProjectDetails = newProjectDetails.deleteIn([
                "subtitle",
                "data",
                dropId,
                subtitleItemId,
              ]);
              shouldBuildSubtitle = true;
            } else {
              let item = newProjectDetails.getIn([
                "subtitle",
                "data",
                dropId,
                subtitleItemId,
              ]);
              const timelineId = subtitleUpdater.get("timelineId");
              let isTimeChanged = false;

              for (const [dataKey, value] of subtitleUpdater.get("data").entrySeq()) {
                item = item?.set(dataKey, value);
                if (dataKey === "start" || dataKey === "end") {
                  isTimeChanged = true;
                }
              }

              newProjectDetails = newProjectDetails.setIn(
                ["subtitle", "data", dropId, subtitleItemId],
                item
              );

              if (isTimeChanged) {
                subtitlesToSort = subtitlesToSort.add(dropId);
                shouldBuildSubtitle = true;
              } else if (timelineId && newProjectDetails.getIn(["localSubtitle", timelineId])) {
                let localItem = newProjectDetails.getIn(["localSubtitle", timelineId]);
                for (const [dataKey, value] of subtitleUpdater.get("data").entrySeq()) {
                  localItem = localItem.set(dataKey, value);
                }
                newProjectDetails = newProjectDetails.setIn(["localSubtitle", timelineId], localItem);
              } else {
                shouldBuildSubtitle = true;
              }
            }
          }
        }
      }
    } else {
      for (const [itemId, itemUpdater] of containerData.entrySeq()) {
        if (itemUpdater.get("isDelete")) {
          // const deletedItem = newProjectDetails.getIn([container, itemId]);
          newProjectDetails = newProjectDetails.deleteIn([container, itemId]);

          if (container === "workspaceItems") {
            itemsMightChangeTrack = true;
          } else if (container === "audios") {
            audiosMightChangeTrack = true;
          }
        } else if (itemUpdater.get("isNew")) {
          newProjectDetails = newProjectDetails.setIn(
            [container, itemId],
            itemUpdater.get("data")
          );

          if (container === "workspaceItems") {
            itemsMightChangeTrack = true;
          } else if (container === "audios") {
            audiosMightChangeTrack = true;
          }
        } else {
          let newItem = newProjectDetails.getIn([container, itemId]);

          if (!newItem) {
            continue; // Skip this item to prevent setting undefined
          }

          for (const [updateKey, updateObj] of itemUpdater.get("data").entrySeq()) {
            let updatePath = [updateKey];
            let updateVal = updateObj;

            if (updateKey === "frameClipImage") {
              updatePath = [
                "clipDetails",
                updateObj.get("clipId"),
                "imgDetails",
              ];
              updateVal = updateObj.get("imgDetails");
            }

            newItem = newItem?.setIn(updatePath, updateVal);

            if (updateKey === "track" && container === "workspaceItems") {
              itemsMightChangeTrack = true;
            } else if (updateKey === "track" && container === "audios") {
              audiosMightChangeTrack = true;
            }
          }

          newProjectDetails = newProjectDetails.setIn(
            [container, itemId],
            newItem
          );
        }

        if (!shouldBuildSubtitle) {
          const prevItem = projectDetails.getIn([container, itemId]);
          const item = newProjectDetails.getIn([container, itemId]);

          if (item) {
            const type = item.get("type");
            const subType = item.get("subType");
            const dropId = item.get("dropId");

            if (
              (isVideoOnly(type, subType) || isAudioOnly(type)) &&
              projectDetails.getIn(["subtitle", "data", dropId])
            ) {
              shouldBuildSubtitle = true;
            }
          }

          if (prevItem) {
            const type = prevItem.get("type");
            const subType = prevItem.get("subType");
            const dropId = prevItem.get("dropId");

            if (
              (isVideoOnly(type, subType) || isAudioOnly(type)) &&
              newProjectDetails.getIn(["subtitle", "data", dropId])
            ) {
              shouldBuildSubtitle = true;
            }
          }
        }
      }
    }
  }

  if (itemsMightChangeTrack) {
    const workspaceItems = newProjectDetails.get("workspaceItems");
    try {
      if (workspaceItems && workspaceItems.size > 0) {
        const sortedItems = workspaceItems.sort((itemA, itemB) => {
          // Safety check for undefined items
          if (!itemA || !itemB) {
            return 0;
          }
          let result = itemA.get("track") - itemB.get("track")
          if (!result) {
            result = itemB.get("enterStart") - itemA.get("enterStart");
          }
          return result;
        })
        newProjectDetails = newProjectDetails.set("workspaceItems", sortedItems);
      }
    } catch (err) {
      console.error("Error in sorting workspace items:", err);
    }
  }

  if (audiosMightChangeTrack) {
    const audios = newProjectDetails.get("audios");
    if (audios) {
      const sortedAudios = audios.sortBy(audio => audio ? audio.get("track") : 0);
      newProjectDetails = newProjectDetails.set("audios", sortedAudios);
    }
  }

  if (subtitlesToSort.size) {
    for (const dropId of subtitlesToSort.valueSeq()) {
      const subtitleData = newProjectDetails.getIn(["subtitle", "data", dropId]);
      if (subtitleData) {
        const sortedSubtitleData = subtitleData.sortBy((item) => item ? item.get("start") : 0);
        newProjectDetails = newProjectDetails.setIn(
          ["subtitle", "data", dropId],
          sortedSubtitleData
        );
      }
    }
  }
  if (shouldBuildSubtitle) {
    newProjectDetails = buildLocalSubtitles(newProjectDetails);
  }

  const smartFocusNewProj = buildLocalSmartfocus(newProjectDetails);
  newProjectDetails = buildLocalAiReframe(smartFocusNewProj);
  return newProjectDetails;
};

/**
 * Function to handle project duration change for autosave
 * @param {object} params
 * @param {object} params.updaterObject updaterObject from autosave/undo
 * @param {object} params.projectDetails project state from redux
 * @returns {object}
 */
export const setUpdaterProjectDuration = (params = {}) => {
  const { projectDetails } = params;
  let { updaterObject } = params;

  // apply timeline changes to get next project duration
  const newProjectDetails = applyTimelineUpdaterObject({
    projectDetails,
    updaterObject,
  });
  const projectDuration = getProjectDuration({ project: newProjectDetails });

  if (projectDuration !== projectDetails.get("duration")) {
    updaterObject = updaterObject.setIn(
      ["project", "data", "duration"],
      projectDuration
    );
  }

  return updaterObject;
};
